import InstructionArchive from "../InstructionArchive";
import { matchesServiceDateRange } from '../../utils/orderServiceDateRange';
import DriverCreatedOrders from "./DriverCreatedOrders";
import OrderSeatBadges from '../OrderSeatBadges';
import TransportAcceptance from "../TransportAcceptance";
import TransportAcceptanceArchive from "../TransportAcceptanceArchive";
import React, { Suspense } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  Car,
  CheckCircle,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Copy,
  CreditCard,
  Download,
  FileText,
  ListFilter,
  LogOut,
  MessageCircle,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
  UserRound,
  UserRoundCog,
  WalletCards,
} from "lucide-react";
import { exportOrderInfoJapanese } from "./orderTextExport";
import { formatPassengerLuggage } from "./orderPassengerDisplay";
import {
  getAdminOrderPaymentBadgeClass,
  getAdminOrderPaymentStatusLabel,
  shouldShowAdminOrderPaymentStatus,
} from "./adminOrderPaymentStatus";
import BrandLogoContainer from "../BrandLogoContainer";
import { mergeDriverTimeOffGroups } from "../../utils/driverTimeOffDisplay";
import { getAdminOrderTimeCategory } from "../../utils/adminOrderTimeCategory";
import bigTaxiDriverLogo from "../../assets/big-taxi-driver-logo.png";

const AdminDispatchers = React.lazy(() => import("./AdminDispatchers"));
const AdminAuditLogs = React.lazy(() => import("./AdminAuditLogs"));
const AdminCreateOrderModal = React.lazy(() => import("./AdminCreateOrderModal"));
const AdminAttendanceCenter = React.lazy(() => import("./AdminAttendanceCenter"));
const AdminDrivers = React.lazy(() => import("./AdminDrivers"));
const AdminOrderList = React.lazy(() => import("./AdminOrderList"));
const AdminSettlements = React.lazy(() => import("./AdminSettlements"));
const AdminStats = React.lazy(() => import("./AdminStats"));
const AdminUsers = React.lazy(() => import("./AdminUsers"));
const BillingDocumentManagement = React.lazy(() => import("./BillingDocumentManagement"));
const BillingExport = React.lazy(() => import("./BillingExport"));
const CityRideGmailImport = React.lazy(() => import("./CityRideGmailImport"));
const DailyReport = React.lazy(() => import("./DailyReport"));
const FrontendLayoutConfig = React.lazy(() => import("./FrontendLayoutConfig"));
const DriverConsolePanel = React.lazy(() => import("./DriverConsolePanel"));
const OrderManagement = React.lazy(() => import("./OrderManagement"));
const PricingConfiguration = React.lazy(() => import("./PricingConfiguration"));
const RetailRewardsAdmin = React.lazy(() => import("./RetailRewardsAdmin"));
const TripCommunicationCenter = React.lazy(() => import("./TripCommunicationCenter"));

const CLOSED_ORDER_STATUSES = new Set(["completed", "cancelled", "deleted", "refunded"]);
const RECENT_MISSING_DRIVER_WINDOW_DAYS = 5;
const DRIVER_ISSUE_LABELS = {
  unable_contact_passenger: "联系不上乘客",
  passenger_no_show: "乘客未出现",
  passenger_late: "乘客迟到",
  flight_or_train_delay: "航班/列车延误",
  pickup_location_problem: "上车地点不明确",
  route_or_destination_changed: "临时变更路线/目的地",
  extra_waiting_time: "需要额外等待",
  luggage_or_item_left: "乘客有遗忘物品",
  vehicle_or_traffic_issue: "车辆/交通异常",
  order_information_problem: "订单信息有误",
  other: "其他问题",
};

function getOrderStatus(order) {
  return String(order?.status || "").toLowerCase();
}

function isLiveOrder(order) {
  return !CLOSED_ORDER_STATUSES.has(getOrderStatus(order));
}

function hasSettlementDispatchPrice(order) {
  const value = order?.assigned_driver?.dispatch_price;
  return value !== null && value !== undefined && value !== "";
}

function canOpenSettlement(order) {
  if (["pending", "paid"].includes(order?.driver_settlement_status)) return true;
  const canSettleByTime = ["in_progress", "completed"].includes(getAdminOrderTimeCategory(order));
  const hasAssignedDriver = Boolean(
    order?.assigned_driver_id ||
    order?.assigned_driver?.driver_id ||
    order?.assigned_driver?.driver_name ||
    order?.assigned_driver?.driver_phone
  );
  return canSettleByTime && hasAssignedDriver && hasSettlementDispatchPrice(order);
}

function getSettlementActionLabel(order) {
  if (order?.driver_settlement_status === "paid") return "已结算";
  if (order?.driver_settlement_status === "pending") return "待转账";
  return "结算";
}

function isMissingDriverAssignment(order) {
  if (!isLiveOrder(order)) return false;
  return !order?.assigned_driver_id && !order?.assigned_driver?.driver_account_id && !order?.assigned_driver?.driver_name;
}

function isPendingRetailOrder(order) {
  if (!isLiveOrder(order)) return false;
  const status = getOrderStatus(order);
  return (
    order?.sales_channel === "retail_public" &&
    ["pending_admin_confirmation", "pending_quote", "quoted", "pending"].includes(status)
  );
}

function isDriverReportedExceptionOrder(order) {
  return Boolean(order?.driver_issue_unresolved || isMobileDriverOvertimeUnhandled(order));
}

function parseExceptionOrderDate(value) {
  if (!value) return null;
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getUpcomingMissingDriverOrders(orders = []) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const windowEnd = new Date(today);
  windowEnd.setDate(today.getDate() + RECENT_MISSING_DRIVER_WINDOW_DAYS);

  return orders
    .filter((order) => {
      if (!isMissingDriverAssignment(order)) return false;
      const departureDate = parseExceptionOrderDate(order.departure_date);
      return departureDate && departureDate >= today && departureDate < windowEnd;
    })
    .sort((a, b) => {
      const left = `${a.departure_date || "9999-12-31"} ${a.pickup_time || "99:99"}`;
      const right = `${b.departure_date || "9999-12-31"} ${b.pickup_time || "99:99"}`;
      return left.localeCompare(right);
    });
}

function formatExceptionOrderDate(order) {
  const rawDate = String(order?.departure_date || "").slice(0, 10);
  if (!rawDate) return "日期未填";
  const [, , month, day] = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/) || [];
  return month && day ? `${month}/${day}` : rawDate;
}

function formatExceptionServiceType(order) {
  return order?.service_type === "charter" ? "包车" : "单送";
}

function getExceptionLocation(order, keys) {
  for (const key of keys) {
    const value = order?.[key];
    if (value) return value;
  }
  return "";
}

function formatExceptionOrderRoute(order) {
  const existingRoute = order?.route || order?.route_summary || order?.itinerary;
  if (existingRoute) return existingRoute;
  const pickup = getExceptionLocation(order, [
    "pickup_location_detail",
    "pickup_location",
    "pickup_address",
    "pickup_area",
  ]);
  const dropoff = getExceptionLocation(order, [
    "dropoff_location_detail",
    "destination_address",
    "dropoff_location",
    "destination",
    "end_location",
    "dropoff_area",
  ]);
  if (pickup && dropoff) return `${pickup} → ${dropoff}`;
  return pickup || dropoff || "行程未填";
}

function getExceptionOrderCustomerName(order) {
  return order?.customer_name || order?.customer_code || "未命名客户";
}

function getDriverReportedExceptionSummary(order) {
  const reasons = [];

  if (order?.driver_issue_unresolved) {
    const issueLabels = getDriverIssueTypeLabels(order);
    const issueNote = String(getDriverIssueReport(order)?.issue_note || "").trim();
    const driverReason = issueLabels.length ? issueLabels.join(" / ") : "司机已报告异常";
    reasons.push(issueNote ? `${driverReason}｜${issueNote}` : driverReason);
  }

  if (isMobileDriverOvertimeUnhandled(order)) {
    reasons.push("司机报备超时");
  }

  return reasons.length ? reasons.join("；") : "司机报备异常";
}

function getFlightAuditExceptionSummary(order) {
  const audit = order?.flight_audit || {};
  return audit.message || "航班号、机场、日期或时间不匹配";
}

function OperationsExceptionPanel({
  orders = [],
  onAssignDriver,
  onOpenExceptionOrder,
}) {
  const missingDriverOrders = getUpcomingMissingDriverOrders(orders);
  const pendingRetailOrders = orders.filter(isPendingRetailOrder).slice(0, 5);
  const driverReportedExceptionOrders = orders.filter(isDriverReportedExceptionOrder).slice(0, 5);
  const flightAuditExceptionOrders = orders.filter((order) => order?.flight_audit_error || order?.flight_audit_status === "error").slice(0, 5);
  return (
    <section
      className="admin-operations-light-ui rounded-[22px] bg-[#0b111b]/70 px-4 py-5 text-slate-100 shadow-[0_28px_80px_-64px_rgba(0,0,0,0.9)] ring-1 ring-white/[0.04] sm:px-5"
      data-testid="operations-exception-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">OPERATIONS</p>
          <h3 className="mt-1 text-xl font-bold">运营待处理</h3>
          <p className="mt-1 text-sm text-slate-300">集中显示未配车、待确认散客、司机报备和航班异常；新消息仍在沟通中心提醒。</p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-slate-700/40">
        <MissingDriverExceptionBucket orders={missingDriverOrders} onAssignDriver={onAssignDriver} />
        <ExceptionBucket
          title="待确认散客"
          orders={pendingRetailOrders}
          badgeClassName="bg-amber-400/15 text-amber-100"
        />
        <DriverReportedExceptionBucket orders={driverReportedExceptionOrders} onOpenOrder={onOpenExceptionOrder} />
        <FlightAuditExceptionBucket orders={flightAuditExceptionOrders} />
      </div>
    </section>
  );
}

function FlightAuditExceptionBucket({ orders }) {
  return (
    <ExceptionBucket
      title="航班异常"
      orders={orders}
      badgeClassName="bg-red-500/20 text-red-100"
      renderOrder={(order) => (
        <div
          key={order.id || order.order_no}
          className="rounded-xl bg-slate-950/60 px-3 py-2.5 text-xs text-slate-200 shadow-[inset_0_0_0_1px_rgba(239,68,68,0.25)]"
          data-testid="flight-audit-exception-order-row"
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-red-200">{formatExceptionOrderDate(order)}</span>
            <span className="font-semibold text-slate-100">{order.flight_number || "航班未填"}</span>
          </div>
          <p className="mt-1 line-clamp-2 font-semibold text-red-100">{getFlightAuditExceptionSummary(order)}</p>
          <p className="mt-1 truncate text-slate-400">
            {order.order_no ? <span className="font-mono">#{order.order_no}</span> : getExceptionOrderCustomerName(order)}
          </p>
        </div>
      )}
    />
  );
}

function MissingDriverExceptionBucket({ orders, onAssignDriver }) {
  return (
    <div className="min-w-0 lg:pr-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <span className="text-sm font-semibold text-slate-100">未配车</span>
          <p className="mt-1 text-xs text-slate-400">今天起 5 天内</p>
        </div>
        <span className="rounded-full bg-cyan-400/15 px-2 py-0.5 text-xs font-bold text-cyan-200">{orders.length}</span>
      </div>
      <div className="mt-3 max-h-52 space-y-2 overflow-y-auto pr-1">
        {orders.length === 0 ? (
          <p className="py-2 text-xs text-slate-500">暂无</p>
        ) : orders.map((order) => (
          <div
            key={order.id || order.order_no}
            className="rounded-xl bg-slate-950/60 px-3 py-2.5 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.12)]"
            data-testid="missing-driver-order-row"
          >
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold text-cyan-200">{formatExceptionOrderDate(order)}</span>
              <span className="font-semibold text-slate-100">{order.pickup_time || "--:--"}</span>
              <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-[11px] font-semibold text-cyan-100">
                {formatExceptionServiceType(order)}
              </span>
            </div>
            <p className="mt-1 truncate text-sm font-semibold text-slate-100">{formatExceptionOrderRoute(order)}</p>
            <div className="mt-2 flex items-center justify-between gap-2">
              <p className="min-w-0 truncate text-xs text-slate-400">
                {getExceptionOrderCustomerName(order)}
                {order.order_no ? <span className="ml-2 font-mono text-slate-500">#{order.order_no}</span> : null}
              </p>
              <button
                type="button"
                onClick={() => onAssignDriver?.(order)}
                className="shrink-0 rounded-md border border-cyan-300/40 bg-cyan-400/10 px-2.5 py-1 text-xs font-semibold text-cyan-100 transition hover:border-cyan-200 hover:bg-cyan-400/20"
                data-testid="missing-driver-assign-button"
              >
                配车
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DriverReportedExceptionBucket({ orders, onOpenOrder }) {
  return (
    <ExceptionBucket
      title="司机报备异常"
      orders={orders}
      badgeClassName="bg-rose-400/15 text-rose-100"
      renderOrder={(order) => (
        <DriverReportedExceptionRow key={order.id || order.order_no} order={order} onOpenOrder={onOpenOrder} />
      )}
    />
  );
}

function DriverReportedExceptionRow({ order, onOpenOrder }) {
  return (
    <button
      type="button"
      onClick={() => onOpenOrder?.(order)}
      className="block w-full rounded-xl bg-slate-950/60 px-3 py-2.5 text-left text-xs text-slate-200 shadow-[inset_0_0_0_1px_rgba(244,63,94,0.2)] transition hover:bg-slate-900/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300"
      data-testid="driver-reported-exception-order-row"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-rose-200">{formatExceptionOrderDate(order)}</span>
        <span className="font-semibold text-slate-100">{order.pickup_time || "--:--"}</span>
        <span className="rounded-full bg-rose-400/15 px-2 py-0.5 text-[11px] font-semibold text-rose-100">
          {getDriverReportedExceptionSummary(order)}
        </span>
      </div>
      <p className="mt-1 truncate text-sm font-semibold text-slate-100">{formatExceptionOrderRoute(order)}</p>
      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-slate-400">
        <p className="min-w-0 truncate">
          {getExceptionOrderCustomerName(order)}
          {order.order_no ? <span className="ml-2 font-mono text-slate-500">#{order.order_no}</span> : null}
        </p>
        <span className="inline-flex shrink-0 items-center gap-0.5 font-semibold text-rose-200">
          打开订单
          <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </button>
  );
}

function ExceptionBucket({ title, orders, badgeClassName, renderOrder }) {
  return (
    <div className="min-w-0 lg:px-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-100">{title}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${badgeClassName}`}>{orders.length}</span>
      </div>
      <div className="mt-3 space-y-2">
        {orders.length === 0 ? (
          <p className="py-2 text-xs text-slate-500">暂无</p>
        ) : renderOrder ? orders.map(renderOrder) : orders.map((order) => (
          <p key={order.id || order.order_no} className="truncate rounded-xl bg-slate-950/60 px-3 py-2.5 text-xs text-slate-200 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.12)]">
            {order.order_no || order.confirmation_code || order.id} · {getExceptionOrderCustomerName(order)}
          </p>
        ))}
      </div>
    </div>
  );
}

function AdminSectionFallback() {
  return (
    <div className="premium-card p-6 text-sm text-muted-foreground">
      读取模块中...
    </div>
  );
}

function LazyAdminSection({ children }) {
  return (
    <Suspense fallback={<AdminSectionFallback />}>
      {children}
    </Suspense>
  );
}

const MOBILE_ORDER_LIMIT = 5;
const ADMIN_MOBILE_CORE_VEHICLE_TYPES = ["alphard", "hiace", "premium_hiace"];
const ADMIN_MOBILE_VEHICLE_TYPE_ALIASES = {
  alpha: "alphard",
  luxury_hiace: "premium_hiace",
};

function getJapanDateKey(offsetDays = 0) {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const base = new Date();
  const japanMidday = new Date(base.toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));
  japanMidday.setHours(12, 0, 0, 0);
  japanMidday.setDate(japanMidday.getDate() + offsetDays);
  return formatter.format(japanMidday);
}

function getOrderDateKey(order) {
  return String(order?.departure_date || order?.service_date || "").slice(0, 10);
}

function sortMobileOrders(orders = []) {
  return [...orders].sort((a, b) => {
    const left = `${getOrderDateKey(a) || "9999-12-31"} ${a.pickup_time || "99:99"}`;
    const right = `${getOrderDateKey(b) || "9999-12-31"} ${b.pickup_time || "99:99"}`;
    return left.localeCompare(right);
  });
}

function isOrderMissingDriver(order) {
  if (CLOSED_ORDER_STATUSES.has(getOrderStatus(order))) return false;
  return !order?.assigned_driver_id && !order?.assigned_driver?.driver_name && !order?.assigned_driver?.driver_account_id;
}

function sortMobileOrdersWithMissingDriverFirst(orders = []) {
  return sortMobileOrders(orders).sort((a, b) => Number(isOrderMissingDriver(b)) - Number(isOrderMissingDriver(a)));
}

function isIssueOrder(order) {
  return Boolean(
    order?.driver_issue_unresolved || isMobileDriverOvertimeUnhandled(order) ||
    order?.driver_complaint_unresolved ||
    order?.flight_audit_error ||
    order?.flight_audit_status === "error" ||
    isPendingRetailOrder(order)
  );
}

function getDriverIssueReport(order) {
  return order?.driver_issue_report || order?.driver_issue || {};
}

function getDriverIssueTypeLabels(order) {
  const report = getDriverIssueReport(order);
  const rawTypes = Array.isArray(report?.issue_types)
    ? report.issue_types
    : Array.isArray(order?.driver_issue_types)
      ? order.driver_issue_types
      : [];
  return rawTypes
    .map((type) => DRIVER_ISSUE_LABELS[type] || String(type || "").trim())
    .filter(Boolean);
}

function getMobileIssueReason(order) {
  const reasons = [];

  if (order?.driver_issue_unresolved) {
    const issueLabels = getDriverIssueTypeLabels(order);
    const issueNote = String(getDriverIssueReport(order)?.issue_note || "").trim();
    const driverReason = issueLabels.length ? issueLabels.join(" / ") : "司机已报告异常";
    reasons.push(issueNote ? `司机异常：${driverReason}｜${issueNote}` : `司机异常：${driverReason}`);
  }

  if (order?.driver_complaint_unresolved) {
    reasons.push("司机投诉待处理");
  }

  if (order?.flight_audit_error || order?.flight_audit_status === "error") {
    reasons.push(`航班异常：${order?.flight_audit?.message || "机场、日期、时间或航班状态需要确认"}`);
  }

  if (isPendingRetailOrder(order)) {
    reasons.push("散客订单待确认");
  }

  return reasons.length ? reasons.join("；") : "异常待处理";
}

function getMobileRoute(order, viewHelpers) {
  const pickup = formatMobileDetailValue(viewHelpers?.formatPickupDisplay?.(order) || order?.pickup_location || order?.pickup_area, "出发地未填");
  const dropoff = formatMobileDetailValue(viewHelpers?.formatDestinationDisplay?.(order) || order?.dropoff_location || order?.dropoff_area || order?.destination, "目的地未填");
  return `${pickup} → ${dropoff}`;
}

function getAssignedDriverName(order) {
  return order?.assigned_driver?.driver_name || order?.assigned_driver_name || order?.driver_name || "";
}

function getMobileOrderCustomer(order) {
  return formatMobileDetailValue(order?.customer_name || order?.customer_code || order?.order_no, "未命名客户");
}

function getMobileOrderCustomerCode(order) {
  return formatMobileDetailValue(order?.customer_code || order?.customer?.customer_code, "未设置");
}

function getMobileOrderLinkedAccount(order) {
  return formatMobileDetailValue(order?.user_email || order?.customer_email || order?.user_id || order?.customer_id, "未关联");
}

function formatMobileDetailValue(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) {
    const text = value.map((item) => formatMobileDetailValue(item, "")).filter(Boolean).join(" / ");
    return text || fallback;
  }
  if (typeof value === "object") {
    return (
      value.name ||
      value.email ||
      value.id ||
      value.order_no ||
      value.customer_code ||
      JSON.stringify(value)
    );
  }
  return String(value);
}

function getMobileOrderDateLabel(order) {
  const dateKey = getOrderDateKey(order);
  if (!dateKey) return "日期未填";
  const todayKey = getJapanDateKey(0);
  const tomorrowKey = getJapanDateKey(1);
  const shortDate = dateKey.slice(5).replace("-", "/");
  if (dateKey === todayKey) return `今天 ${shortDate}`;
  if (dateKey === tomorrowKey) return `明天 ${shortDate}`;
  return shortDate;
}

function getMobileOrderStatusLabel(order) {
  const status = getOrderStatus(order);
  const labels = {
    completed: "已完成",
    cancelled: "已取消",
    deleted: "已删除",
    refunded: "已退款",
    confirmed: "已确认",
    pending: "待处理",
    pending_quote: "报价中",
    pending_admin_confirmation: "待确认",
  };
  return labels[status] || status || "状态未填";
}

function getMobilePaymentStatusLabel(order) {
  return getAdminOrderPaymentStatusLabel(order);
}

function getMobilePaymentStatusClass(order) {
  return getAdminOrderPaymentBadgeClass(order);
}

function shouldShowMobilePaymentStatus(order) {
  return shouldShowAdminOrderPaymentStatus(order);
}

function handleMobileOrderAction(event, action, order) {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  action?.(order);
}

function handleMobilePaymentAction(event, action, order) {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  if (!order?.id) return;
  action?.([order.id]);
}

function handleMobileDeleteOrderAction(event, action, order) {
  event?.preventDefault?.();
  event?.stopPropagation?.();
  if (!order?.id) return;
  action?.(order.id);
}

function matchesMobileOrderSearch(order, keyword, viewHelpers) {
  const query = String(keyword || "").trim().toLowerCase();
  if (!query) return true;
  const target = [
    getMobileOrderCustomer(order),
    order?.order_no,
    order?.customer_code,
    getAssignedDriverName(order),
    getMobileRoute(order, viewHelpers),
    getMobileOrderDateLabel(order),
    order?.pickup_time,
    order?.flight_number,
  ].filter(Boolean).join(" ").toLowerCase();
  return target.includes(query);
}

function hasReportedEndTime(value) {
  return String(value || "").trim().length > 0;
}

function parseClockMinutes(value) {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

function isMobileDriverOvertimeUnhandled(order) {
  if (order?.service_type !== "charter" || !hasReportedEndTime(order?.reported_end_time) || !order?.pickup_time || !order?.selected_hours) {
    return false;
  }
  const startMinutes = parseClockMinutes(order.pickup_time);
  let endMinutes = parseClockMinutes(order.reported_end_time);
  const bookedHours = Number(order.selected_hours);
  if (startMinutes === null || endMinutes === null || !Number.isFinite(bookedHours)) return false;
  if (endMinutes < startMinutes) endMinutes += 24 * 60;
  return (endMinutes - startMinutes) / 60 > bookedHours && order?.overtime_handled !== true;
}

function isMobileCustomerOvertimeChargePending(order) {
  if (order?.service_type !== "charter" || !hasReportedEndTime(order?.reported_end_time) || !order?.pickup_time || !order?.selected_hours) {
    return false;
  }
  const startMinutes = parseClockMinutes(order.pickup_time);
  let endMinutes = parseClockMinutes(order.reported_end_time);
  const bookedHours = Number(order.selected_hours);
  if (startMinutes === null || endMinutes === null || !Number.isFinite(bookedHours)) return false;
  if (endMinutes < startMinutes) endMinutes += 24 * 60;
  return (endMinutes - startMinutes) / 60 > bookedHours && order?.customer_overtime_charge_applied !== true;
}

function getMobileOrderTimeCategory(order) {
  const status = getOrderStatus(order);
  if (status === "completed") return "completed";
  if (!isLiveOrder(order)) return "cancelled";
  const dateKey = getOrderDateKey(order);
  const todayKey = getJapanDateKey(0);
  if (!dateKey || dateKey > todayKey) return "not_started";
  if (dateKey < todayKey) return "completed";
  return "in_progress";
}

function getMobileOrderMonthOptions(orders = []) {
  return [...new Set(orders.map((order) => getOrderDateKey(order).slice(0, 7)).filter(Boolean))].sort().reverse();
}

function normalizeMobileAccountLookupValue(value) {
  return String(value || "").trim().toLowerCase();
}

function getMobileTravelAgencyAccountTokens(account = {}) {
  return [
    account.email,
    account.customer_code,
    account.id,
  ].map(normalizeMobileAccountLookupValue).filter(Boolean);
}

function getMobileOrderAccountTokens(order = {}) {
  return [
    order.user_email,
    order.customer_email,
    order.linked_user_email,
    order.user_id,
    order.customer_id,
    order.customer_code,
    order.customer?.email,
    order.customer?.id,
    order.customer?.customer_code,
  ].map(normalizeMobileAccountLookupValue).filter(Boolean);
}

function getMobileTravelAgencyAccountOptionLabel(user) {
  const displayName = user?.company_name || user?.name || user?.email || "未命名旅行社";
  const meta = [user?.customer_code, user?.email && user.email !== displayName ? user.email : ""].filter(Boolean).join(" / ");
  return meta ? `${displayName} (${meta})` : displayName;
}

function getMobileTravelAgencyAccountOptions(allUsers = []) {
  return allUsers
    .filter((user) => user?.role === "supplier" && user?.status !== "deleted")
    .map((user) => {
      const tokens = getMobileTravelAgencyAccountTokens(user);
      return {
        value: tokens.join("|"),
        label: getMobileTravelAgencyAccountOptionLabel(user),
      };
    })
    .filter((option) => option.value)
    .sort((a, b) => a.label.localeCompare(b.label, "ja"));
}

function matchesMobileTravelAgencyAccount(order, selectedAccountValue) {
  const selectedTokens = String(selectedAccountValue || "").split("|").map(normalizeMobileAccountLookupValue).filter(Boolean);
  if (!selectedTokens.length) return true;
  const orderTokens = getMobileOrderAccountTokens(order);
  return selectedTokens.some((token) => orderTokens.includes(token));
}

function getMobileOrderDriverOptions(orders = []) {
  return [...new Set(orders.map(getAssignedDriverName).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ja"));
}

function getMobileOrderVehicleTypeKey(order) {
  const rawType = String(order?.vehicle_type || order?.vehicle_type_name || order?.car_type || "").trim();
  return ADMIN_MOBILE_VEHICLE_TYPE_ALIASES[rawType] || rawType;
}

function getMobileOrderVehicleTypeOptions(orders = [], viewHelpers) {
  return [...new Set([...ADMIN_MOBILE_CORE_VEHICLE_TYPES, ...orders.map(getMobileOrderVehicleTypeKey).filter(Boolean)])]
    .sort((a, b) => a.localeCompare(b, "ja"))
    .map((vehicleType) => ({
      value: vehicleType,
      label: viewHelpers?.getVehicleTypeName?.(vehicleType) || vehicleType,
    }));
}

function filterMobileOrders(orders = [], {
  dateFilter,
  statusFilter,
  mobileDriverNameFilter,
  mobileVehicleTypeFilter,
  keyword,
  mobileMonthFilter,
  mobileCustomerFilter,
  driverReportOnly,
  mobileSortByDate,
}, viewHelpers) {
  const todayKey = getJapanDateKey(0);
  const tomorrowKey = getJapanDateKey(1);
  const filtered = orders.filter((order) => {
    const dateKey = getOrderDateKey(order);
    const status = getOrderStatus(order);
    if (dateFilter === "today" && dateKey !== todayKey) return false;
    if (dateFilter === "tomorrow" && dateKey !== tomorrowKey) return false;
    if (dateFilter === "future" && (!dateKey || dateKey <= tomorrowKey)) return false;
    if (mobileMonthFilter && !dateKey.startsWith(mobileMonthFilter)) return false;
    if (mobileCustomerFilter && !matchesMobileTravelAgencyAccount(order, mobileCustomerFilter)) return false;
    if (statusFilter === "not_started" && getMobileOrderTimeCategory(order) !== "not_started") return false;
    if (statusFilter === "in_progress" && getMobileOrderTimeCategory(order) !== "in_progress") return false;
    if (statusFilter === "completed" && status !== "completed") return false;
    if (statusFilter === "missing_driver" && !isOrderMissingDriver(order)) return false;
    if (mobileDriverNameFilter && getAssignedDriverName(order) !== mobileDriverNameFilter) return false;
    if (mobileVehicleTypeFilter && getMobileOrderVehicleTypeKey(order) !== mobileVehicleTypeFilter) return false;
    if (driverReportOnly && !isMobileDriverOvertimeUnhandled(order)) return false;
    return matchesMobileOrderSearch(order, keyword, viewHelpers);
  });
  return mobileSortByDate ? sortMobileOrders(filtered) : filtered;
}

function AdminMobileDashboard({
  orders = [],
  pendingUsers = [],
  driverAccounts = [],
  viewHelpers,
  onAssignDriver,
  onDuplicateOrder,
  onDeleteOrder,
  onDownloadSignboard,
  onEditOrder,
  onCreateDriverComplaint,
  onGeneratePaymentPage,
  onApplyCustomerOvertimeCharge,
  onMarkOvertimeHandled,
  onOpenOrderDetail,
  onOpenAdminOrderForm,
  onOpenTripCommunication,
  onSettleOrder,
  onOpenDriverReportOrders,
  onSelectAdminTab,
  onLogout,
}) {
  const todayKey = getJapanDateKey(0);
  const tomorrowKey = getJapanDateKey(1);
  const todayOrders = sortMobileOrders(orders.filter((order) => getOrderDateKey(order) === todayKey));
  const tomorrowOrders = sortMobileOrders(orders.filter((order) => getOrderDateKey(order) === tomorrowKey));
  const activeTodayOrders = todayOrders.filter(isLiveOrder);
  const activeTomorrowOrders = tomorrowOrders.filter(isLiveOrder);
  const urgentOrders = sortMobileOrders([...activeTodayOrders, ...activeTomorrowOrders].filter(isIssueOrder));
  const driverReportPendingOrders = sortMobileOrders(orders.filter(isMobileDriverOvertimeUnhandled));
  const pendingUserPreview = pendingUsers.slice(0, 3);
  const [homeDayFilter, setHomeDayFilter] = React.useState("today");
  const [homeMissingDriverFirst, setHomeMissingDriverFirst] = React.useState(false);
  const baseSelectedHomeOrders = homeDayFilter === "tomorrow" ? tomorrowOrders : todayOrders;
  const selectedHomeOrders = homeMissingDriverFirst ? sortMobileOrdersWithMissingDriverFirst(baseSelectedHomeOrders) : baseSelectedHomeOrders;
  const selectedHomeMissingDriverCount = baseSelectedHomeOrders.filter(isOrderMissingDriver).length;
  const selectedHomeTitle = homeDayFilter === "tomorrow" ? "次日订单" : "当日订单";
  const selectedHomeCaption = homeDayFilter === "tomorrow" ? "明天全部订单，含已完成" : "当天全部订单，含已完成";
  const selectedHomeEmpty = homeDayFilter === "tomorrow" ? "明天暂无订单" : "今天暂无订单";

  return (
    <div className="admin-mobile-home" data-testid="admin-mobile-home">
      <header className="admin-mobile-header">
        <div className="admin-mobile-brand-lockup">
          <BrandLogoContainer className="admin-mobile-brand-mark" src={bigTaxiDriverLogo} alt="Big Taxi" />
          <div className="admin-mobile-brand-copy">
            <h1 className="driver-brand-title-gold">BIG TAXI</h1>
            <p className="driver-brand-subtitle-white">Private Transfer & Charter</p>
            <p className="driver-brand-page-meta">管理员后台 · 日本时间 {todayKey}</p>
          </div>
        </div>
      </header>

      <section className="admin-mobile-priority-grid" aria-label="重点订单">
        <AdminMobileDayCard title="当日订单" dateKey={todayKey} orders={todayOrders} active={homeDayFilter === "today"} onClick={() => setHomeDayFilter("today")} />
        <AdminMobileDayCard title="次日订单" dateKey={tomorrowKey} orders={tomorrowOrders} active={homeDayFilter === "tomorrow"} onClick={() => setHomeDayFilter("tomorrow")} />
      </section>

      {urgentOrders.length > 0 && (
        <section className="admin-mobile-section">
          <div className="admin-mobile-section-title">
            <div>
              <h2>待处理</h2>
              <p>司机异常、超时报备、待确认</p>
            </div>
            <span>{urgentOrders.length}</span>
          </div>
          <div className="admin-mobile-order-list">
            {urgentOrders.slice(0, 3).map((order) => (
              <AdminMobileOrderCard
                key={order.id || order.order_no}
                order={order}
                viewHelpers={viewHelpers}
                onAssignDriver={onAssignDriver}
                onDuplicateOrder={onDuplicateOrder}
                onDeleteOrder={onDeleteOrder}
                onDownloadSignboard={onDownloadSignboard}
                onEditOrder={onEditOrder}
                onCreateDriverComplaint={onCreateDriverComplaint}
                onGeneratePaymentPage={onGeneratePaymentPage}
                onApplyCustomerOvertimeCharge={onApplyCustomerOvertimeCharge}
                onMarkOvertimeHandled={onMarkOvertimeHandled}
                onOpenOrderDetail={onOpenOrderDetail}
                onOpenTripCommunication={onOpenTripCommunication}
                onSettleOrder={onSettleOrder}
              />
            ))}
          </div>
        </section>
      )}

      <section className="admin-mobile-section">
        <div className="admin-mobile-section-title">
          <div>
            <h2>{selectedHomeTitle}</h2>
            <p>{selectedHomeCaption}</p>
          </div>
          <button
            type="button"
            className={homeMissingDriverFirst ? "is-active" : ""}
            onClick={() => setHomeMissingDriverFirst((value) => !value)}
          >
            未配车 {selectedHomeMissingDriverCount}
          </button>
        </div>
        <div className="admin-mobile-order-list">
          {selectedHomeOrders.length === 0 ? (
            <p className="admin-mobile-empty">{selectedHomeEmpty}</p>
          ) : selectedHomeOrders.map((order) => (
            <AdminMobileOrderCard
              key={order.id || order.order_no}
              order={order}
              viewHelpers={viewHelpers}
              onAssignDriver={onAssignDriver}
              onDuplicateOrder={onDuplicateOrder}
              onDeleteOrder={onDeleteOrder}
              onDownloadSignboard={onDownloadSignboard}
              onEditOrder={onEditOrder}
              onCreateDriverComplaint={onCreateDriverComplaint}
              onGeneratePaymentPage={onGeneratePaymentPage}
              onApplyCustomerOvertimeCharge={onApplyCustomerOvertimeCharge}
              onMarkOvertimeHandled={onMarkOvertimeHandled}
              onOpenOrderDetail={onOpenOrderDetail}
              onOpenTripCommunication={onOpenTripCommunication}
              onSettleOrder={onSettleOrder}
            />
          ))}
        </div>
      </section>

      <section
        className="admin-mobile-section admin-mobile-pending-users"
        role="button"
        tabIndex={0}
        onClick={() => onSelectAdminTab?.("users")}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelectAdminTab?.("users");
          }
        }}
      >
        <div className="admin-mobile-section-title">
          <div>
            <h2>待审核客户</h2>
            <p>点击进入客户管理</p>
          </div>
          <span>{pendingUsers.length}</span>
        </div>
        {pendingUserPreview.length === 0 ? (
          <p className="admin-mobile-empty">暂无待审核客户</p>
        ) : (
          <div className="admin-mobile-pending-user-list">
            {pendingUserPreview.map((user) => (
              <div key={user.email || user.id || user.name} className="admin-mobile-pending-user">
                <UserRoundCog className="h-4 w-4" />
                <span>{user.company_name || user.name || user.email || "未命名客户"}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="admin-mobile-section admin-mobile-summary-row">
        <button type="button" onClick={() => onSelectAdminTab?.("users")}>
          <span>{pendingUsers.length}</span>
          <p>待审核客户</p>
        </button>
        <button type="button" onClick={() => onSelectAdminTab?.("drivers")}>
          <span>{driverAccounts.length}</span>
          <p>司机账号</p>
        </button>
        <button type="button" onClick={() => onSelectAdminTab?.("orders")}>
          <span>{activeTomorrowOrders.length}</span>
          <p>明日订单</p>
        </button>
        <button type="button" onClick={() => onOpenDriverReportOrders?.()}>
          <span>{driverReportPendingOrders.length}</span>
          <p>司机汇报待处理</p>
        </button>
      </section>

      <section className="admin-mobile-logout-section" aria-label="退出登录">
        <button type="button" className="admin-mobile-logout" onClick={onLogout}>
          <LogOut className="h-5 w-5" />
          退出登录
        </button>
      </section>
    </div>
  );
}

function AdminMobileDayCard({ title, dateKey, orders, active = false, onClick }) {
  const missingDriverCount = orders.filter(isOrderMissingDriver).length;
  const issueCount = orders.filter(isIssueOrder).length;
  const completedCount = orders.filter((order) => getOrderStatus(order) === "completed").length;
  return (
    <button type="button" className={`admin-mobile-day-card ${active ? "is-active" : ""}`} onClick={onClick}>
      <div>
        <span>{dateKey.slice(5).replace("-", "/")}</span>
        <h2>{title}</h2>
      </div>
      <strong>{orders.length}</strong>
      <p>
        未配车 {missingDriverCount}
        <span>异常 {issueCount}</span>
        <span>完成 {completedCount}</span>
      </p>
    </button>
  );
}

function AdminMobileOrderCard({
  order,
  viewHelpers,
  onAssignDriver,
  onDuplicateOrder,
  onDeleteOrder,
  onDownloadSignboard,
  onEditOrder,
  onCreateDriverComplaint,
  onGeneratePaymentPage,
  onApplyCustomerOvertimeCharge,
  onMarkOvertimeHandled,
  onOpenOrderDetail,
  onOpenTripCommunication,
  onSettleOrder,
}) {
  const assignedDriver = getAssignedDriverName(order);
  const vehicleName = viewHelpers?.getVehicleTypeName?.(order?.vehicle_type) || order?.vehicle_type || "车型未填";
  return (
    <article className="admin-mobile-order-card" data-testid="admin-mobile-order-card">
      <OrderSeatBadges order={order} />
      <div className="admin-mobile-order-main">
        <time>
          <span>{getMobileOrderDateLabel(order)}</span>
          {order.pickup_time || "--:--"}
        </time>
        <div>
          <div className={assignedDriver ? "admin-mobile-order-driver-line is-ok" : "admin-mobile-order-driver-line is-warn"}>
            <UserRound className="h-3.5 w-3.5" />
            {assignedDriver || "未配车"}
          </div>
          <h3>{getMobileOrderCustomer(order)}</h3>
          <p>{getMobileRoute(order, viewHelpers)}</p>
        </div>
      </div>
      <div className="admin-mobile-order-meta">
        <span><CalendarDays className="h-3.5 w-3.5" />{getMobileOrderDateLabel(order)}</span>
        <span className="admin-mobile-order-account-line">客户代码 {getMobileOrderCustomerCode(order)}</span>
        <span className="admin-mobile-order-account-line">关联账户 {getMobileOrderLinkedAccount(order)}</span>
        <span><Car className="h-3.5 w-3.5" />{vehicleName}</span>
        {order.service_type === "charter" && (
          <span><Clock3 className="h-3.5 w-3.5" />包车 {order.selected_hours || "-"} 小时</span>
        )}
        <span className={isLiveOrder(order) ? "is-ok" : ""}>{getMobileOrderStatusLabel(order)}</span>
        {shouldShowMobilePaymentStatus(order) && (
          <span className={`admin-mobile-order-payment-status ${getMobilePaymentStatusClass(order)}`}>
            <CreditCard className="h-3.5 w-3.5" />{getMobilePaymentStatusLabel(order)}
          </span>
        )}
        {isIssueOrder(order) && (
          <span className="is-danger admin-mobile-order-issue-reason">
            <AlertTriangle className="h-3.5 w-3.5" />
            {getMobileIssueReason(order)}
          </span>
        )}
        {isMobileDriverOvertimeUnhandled(order) && <span className="is-danger"><AlertTriangle className="h-3.5 w-3.5" />司机汇报待处理</span>}
        {order?.customer_overtime_charge_applied === true && (
          <span><CircleDollarSign className="h-3.5 w-3.5" />超时费 ¥{Number(order.customer_overtime_fee || 0).toLocaleString()}</span>
        )}
      </div>
      <div className="admin-mobile-order-actions admin-mobile-order-action-grid">
        {isMobileCustomerOvertimeChargePending(order) && (
          <button type="button" onClick={() => onApplyCustomerOvertimeCharge?.(order.id)} aria-label="填写超时费并加入客户账单">
            <CircleDollarSign className="h-4 w-4" />
            <span>加超时费</span>
          </button>
        )}
        {isMobileDriverOvertimeUnhandled(order) && (
          <button type="button" onClick={() => onMarkOvertimeHandled?.(order.id)} aria-label="标记司机汇报已处理">
            <CheckCircle className="h-4 w-4" />
            <span>已处理</span>
          </button>
        )}
        <button type="button" onClick={() => onEditOrder?.(order)} aria-label="编辑订单">
          <Pencil className="h-4 w-4" />
          <span>编辑</span>
        </button>
        <button type="button" onClick={() => onAssignDriver?.(order)} aria-label="配车">
          <UserPlus className="h-4 w-4" />
          <span>配车</span>
        </button>
        <button type="button" onClick={(event) => handleMobileOrderAction(event, onCreateDriverComplaint, order)} aria-label="投诉扣分">
          <AlertTriangle className="h-4 w-4" />
          <span>投诉</span>
        </button>
        <button type="button" onClick={(event) => handleMobilePaymentAction(event, onGeneratePaymentPage, order)} aria-label="创建付款链接">
          <CreditCard className="h-4 w-4" />
          <span>付款</span>
        </button>
        <button type="button" onClick={() => onOpenOrderDetail?.(order)} aria-label="详情">
          <FileText className="h-4 w-4" />
          <span>详情</span>
        </button>
        <button type="button" onClick={() => onOpenTripCommunication?.(order)} aria-label="沟通">
          <MessageCircle className="h-4 w-4" />
          <span>沟通</span>
        </button>
        {canOpenSettlement(order) && (
          <button
            type="button"
            onClick={() => onSettleOrder?.(order)}
            disabled={["pending", "paid"].includes(order?.driver_settlement_status)}
            aria-label="结算"
          >
            <WalletCards className="h-4 w-4" />
            <span>{getSettlementActionLabel(order)}</span>
          </button>
        )}
        <button type="button" onClick={() => onDuplicateOrder?.(order)} aria-label="复制">
          <Copy className="h-4 w-4" />
          <span>复制</span>
        </button>
        <button type="button" onClick={() => exportOrderInfoJapanese(order)} aria-label="文本">
          <FileText className="h-4 w-4" />
          <span>文本</span>
        </button>
        <TransportAcceptance order={order} admin />
        <button type="button" onClick={() => onDownloadSignboard?.(order)} aria-label="举牌">
          <Download className="h-4 w-4" />
          <span>举牌</span>
        </button>
        <button type="button" onClick={(event) => handleMobileDeleteOrderAction(event, onDeleteOrder, order)} aria-label="删除">
          <Trash2 className="h-4 w-4" />
          <span>删除</span>
        </button>
      </div>
    </article>
  );
}

function AdminMobileOrdersPage({
  allUsers = [],
  orders = [],
  initialQuickView = "",
  viewHelpers,
  onAssignDriver,
  onDuplicateOrder,
  onDeleteOrder,
  onDownloadSignboard,
  onEditOrder,
  onCreateDriverComplaint,
  onGeneratePaymentPage,
  onApplyCustomerOvertimeCharge,
  onMarkOvertimeHandled,
  onOpenOrderDetail,
  onOpenAdminOrderForm,
  onOpenTripCommunication,
  onSettleOrder,
  onInitialQuickViewChange,
  onRefreshData,
}) {
  const [filter, setFilter] = React.useState("all");
  const [dateFilter, setDateFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [mobileDriverNameFilter, setMobileDriverNameFilter] = React.useState("");
  const [mobileVehicleTypeFilter, setMobileVehicleTypeFilter] = React.useState("");
  const [mobileMonthFilter, setMobileMonthFilter] = React.useState("");
  const [mobileCustomerFilter, setMobileCustomerFilter] = React.useState("");
  const [mobileSortByDate, setMobileSortByDate] = React.useState(true);
  const [keyword, setKeyword] = React.useState("");
  const todayKey = getJapanDateKey(0);
  const tomorrowKey = getJapanDateKey(1);
  const mobileMonthOptions = getMobileOrderMonthOptions(orders);
  const mobileDriverOptions = getMobileOrderDriverOptions(orders);
  const mobileVehicleTypeOptions = getMobileOrderVehicleTypeOptions(orders, viewHelpers);
  const mobileTravelAgencyOptions = getMobileTravelAgencyAccountOptions(allUsers);
  const isDriverReportQuickView = filter === "driver_report_pending" || initialQuickView === "driver_report_pending";
  const quickFilters = {
    all: { dateFilter: "all", statusFilter: "all" },
    today: { dateFilter: "today", statusFilter: "all" },
    tomorrow: { dateFilter: "tomorrow", statusFilter: "all" },
  };
  const filteredOrders = filterMobileOrders(orders, {
    dateFilter,
    statusFilter,
    mobileDriverNameFilter,
    mobileVehicleTypeFilter,
    keyword,
    mobileMonthFilter,
    mobileCustomerFilter,
    driverReportOnly: isDriverReportQuickView,
    mobileSortByDate,
  }, viewHelpers);

  React.useEffect(() => {
    if (initialQuickView === "driver_report_pending") {
      setFilter("driver_report_pending");
      setDateFilter("all");
      setStatusFilter("all");
      setMobileMonthFilter("");
      setMobileDriverNameFilter("");
      setMobileVehicleTypeFilter("");
      setMobileCustomerFilter("");
      setKeyword("");
      onInitialQuickViewChange?.("");
    }
  }, [initialQuickView, onInitialQuickViewChange]);
  const setQuickFilter = (value) => {
    const next = quickFilters[value] || quickFilters.all;
    setFilter(value);
    setDateFilter(next.dateFilter);
    setStatusFilter(next.statusFilter);
  };
  const resetMobileOrderFilters = () => {
    setQuickFilter("all");
    setMobileMonthFilter("");
    setMobileDriverNameFilter("");
    setMobileVehicleTypeFilter("");
    setMobileCustomerFilter("");
    setMobileSortByDate(true);
    setKeyword("");
  };

  const quickFilterCounts = {
    all: orders.length,
    today: orders.filter((order) => getOrderDateKey(order) === todayKey).length,
    tomorrow: orders.filter((order) => getOrderDateKey(order) === tomorrowKey).length,
  };
  const hasAdvancedFilters = Boolean(
    mobileMonthFilter ||
    mobileDriverNameFilter ||
    mobileVehicleTypeFilter ||
    mobileCustomerFilter ||
    !mobileSortByDate ||
    keyword ||
    filter !== "all"
  );

  return (
    <div className="admin-mobile-home admin-mobile-orders-page" data-testid="admin-mobile-orders-page">
      <header className="admin-mobile-page-header">
        <div>
          <span>Orders</span>
          <h1>订单</h1>
          <p>日本时间 {todayKey} · 共 {orders.length} 单</p>
        </div>
        <button type="button" onClick={onRefreshData} aria-label="刷新订单数据">
          <RefreshCw className="h-5 w-5" />
        </button>
      </header>

      <section className="admin-mobile-segmented" aria-label="订单筛选">
        {[
          ["all", "全部", quickFilterCounts.all],
          ["today", "当日", quickFilterCounts.today],
          ["tomorrow", "次日", quickFilterCounts.tomorrow],
        ].map(([value, label, count]) => (
          <button
            key={value}
            type="button"
            className={filter === value ? "is-active" : ""}
            onClick={() => setQuickFilter(value)}
          >
            <span>{label}</span>
            <strong>{count}</strong>
          </button>
        ))}
      </section>

      <section className="admin-mobile-filter-panel" aria-label="订单完整筛选">
        <div className="admin-mobile-filter-title">
          <ListFilter className="h-4 w-4" />
          <span>筛选</span>
        </div>
        <div className="admin-mobile-filter-row">
          <label data-testid="admin-mobile-customer-name-filter">
            <span>关键词</span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索客户、订单号、司机、地点"
              aria-label="订单关键词筛选"
              data-testid="admin-mobile-customer-name-input"
            />
          </label>
          <label>
            <span>日期</span>
            <select value={dateFilter} onChange={(event) => { setDateFilter(event.target.value); setFilter("custom"); }}>
              <option value="all">全部日期</option>
              <option value="today">当日</option>
              <option value="tomorrow">次日</option>
              <option value="future">次日以后</option>
            </select>
          </label>
          <label>
            <span>状态</span>
            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setFilter("custom"); }}>
              <option value="all">全部状态</option>
              <option value="not_started">未开始</option>
              <option value="in_progress">进行中</option>
              <option value="completed">已完成</option>
              <option value="missing_driver">未配车</option>
            </select>
          </label>
          <label>
            <span>司机姓名</span>
            <select value={mobileDriverNameFilter} onChange={(event) => { setMobileDriverNameFilter(event.target.value); setFilter("custom"); }}>
              <option value="">全部司机姓名</option>
              {mobileDriverOptions.map((driverName) => (
                <option key={driverName} value={driverName}>{driverName}</option>
              ))}
            </select>
          </label>
          <label>
            <span>车型</span>
            <select value={mobileVehicleTypeFilter} onChange={(event) => { setMobileVehicleTypeFilter(event.target.value); setFilter("custom"); }}>
              <option value="">全部车型</option>
              {mobileVehicleTypeOptions.map((vehicleType) => (
                <option key={vehicleType.value} value={vehicleType.value}>{vehicleType.label}</option>
              ))}
            </select>
          </label>
          <label>
            <span>月份</span>
            <select value={mobileMonthFilter} onChange={(event) => { setMobileMonthFilter(event.target.value); setFilter("custom"); }}>
              <option value="">全部月份</option>
              {mobileMonthOptions.map((month) => <option key={month} value={month}>{month}</option>)}
            </select>
          </label>
          <label data-testid="admin-mobile-travel-agency-filter">
            <span>旅行社</span>
            <select
              value={mobileCustomerFilter}
              onChange={(event) => { setMobileCustomerFilter(event.target.value); setFilter("custom"); }}
              aria-label="按旅行社账号筛选"
              data-testid="admin-mobile-travel-agency-account-select"
            >
              <option value="">全部旅行社（会社名/氏名）</option>
              {mobileTravelAgencyOptions.map((account) => (
                <option key={account.value} value={account.value}>{account.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="admin-mobile-filter-toggle-grid" aria-label="订单筛选开关">
          <button type="button" className={mobileSortByDate ? "is-active" : ""} onClick={() => setMobileSortByDate((value) => !value)}>
            按日期排序
          </button>
        </div>
        {hasAdvancedFilters && (
          <button type="button" className="admin-mobile-filter-reset" onClick={resetMobileOrderFilters}>
            重置筛选
          </button>
        )}
      </section>

      <section className="admin-mobile-section">
        <div className="admin-mobile-section-title">
          <div>
                <h2>{isDriverReportQuickView ? "司机汇报待处理" : (filter === "all" ? "全部订单" : "筛选结果")}</h2>
                <p>{isDriverReportQuickView ? "可直接标记已处理" : "按出发时间排序"}</p>
          </div>
          <span>{filteredOrders.length}</span>
        </div>
        <div className="admin-mobile-order-list">
          {filteredOrders.length === 0 ? (
            <p className="admin-mobile-empty">暂无符合条件的订单</p>
          ) : filteredOrders.map((order) => (
            <AdminMobileOrderCard
              key={order.id || order.order_no}
              order={order}
              viewHelpers={viewHelpers}
              onAssignDriver={onAssignDriver}
              onDuplicateOrder={onDuplicateOrder}
              onDeleteOrder={onDeleteOrder}
              onDownloadSignboard={onDownloadSignboard}
              onEditOrder={onEditOrder}
                  onCreateDriverComplaint={onCreateDriverComplaint}
                  onGeneratePaymentPage={onGeneratePaymentPage}
                  onApplyCustomerOvertimeCharge={onApplyCustomerOvertimeCharge}
                  onMarkOvertimeHandled={onMarkOvertimeHandled}
                  onOpenOrderDetail={onOpenOrderDetail}
                  onOpenTripCommunication={onOpenTripCommunication}
                  onSettleOrder={onSettleOrder}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function getMobileDriverName(driver) {
  return driver?.driver_name || driver?.name || driver?.full_name || driver?.email || "未命名司机";
}

function getMobileDriverContact(driver) {
  return driver?.phone || driver?.phone_number || driver?.mobile || driver?.email || "联系方式未填";
}

function getMobileDriverVehicle(driver) {
  return driver?.vehicle_type_name || driver?.vehicle_name || driver?.vehicle_type || driver?.car_model || "车辆未填";
}

function getMobileDriverArea(driver) {
  return driver?.service_area || driver?.base_area || driver?.dispatcher_name || driver?.city || "区域未填";
}

function getOrderAssignedDriverId(order) {
  return order?.assigned_driver_id || order?.assigned_driver?.driver_account_id || order?.driver_account_id || order?.driver_id || "";
}

const MOBILE_DISPATCH_TIME_AXIS_HOURS = [6, 10, 14, 18, 22];

function getMobileDispatchDays() {
  return [0, 1, 2].map((offset) => {
    const dateKey = getJapanDateKey(offset);
    return {
      dateKey,
      label: offset === 0 ? "今天" : offset === 1 ? "明天" : dateKey.slice(5).replace("-", "/"),
    };
  });
}

function getMobileDispatchMinutes(order) {
  const [hour, minute] = String(order?.pickup_time || order?.time || "09:00").split(":").map((part) => Number(part));
  const safeHour = Number.isFinite(hour) ? hour : 9;
  const safeMinute = Number.isFinite(minute) ? minute : 0;
  return safeHour * 60 + safeMinute;
}

function isMobileDispatchExtraSchedule(item) {
  return item?.source_type === "dispatcher_extra" || item?.source_type === "driver_extra";
}

function getMobileDispatchDateKey(item) {
  return String(isMobileDispatchExtraSchedule(item) ? item?.date : getOrderDateKey(item)).slice(0, 10);
}

function getMobileDispatchTime(item) {
  return item?.pickup_time || item?.time || "--:--";
}

function getMobileDispatchSourceLabel(item) {
  if (item?.source_type === "dispatcher_extra") {
    return item?.created_by_name ? `调度预约 · ${item.created_by_name}` : "调度预约";
  }
  if (item?.source_type === "driver_extra") return "司机追加";
  return "正式订单";
}

function sortMobileDispatchItems(items = []) {
  return [...items].sort((a, b) => {
    const left = `${getMobileDispatchDateKey(a) || "9999-12-31"} ${getMobileDispatchTime(a) || "99:99"}`;
    const right = `${getMobileDispatchDateKey(b) || "9999-12-31"} ${getMobileDispatchTime(b) || "99:99"}`;
    return left.localeCompare(right);
  });
}

function getMobileDispatchBlockStyle(order) {
  const startMinutes = Math.max(6 * 60, Math.min(22 * 60, getMobileDispatchMinutes(order)));
  const durationMinutes = order?.service_type === "charter"
    ? Math.max(90, Number(order.selected_hours || 5) * 60)
    : 90;
  const endMinutes = Math.max(startMinutes + 45, Math.min(22 * 60, startMinutes + durationMinutes));
  const totalMinutes = 16 * 60;
  const left = ((startMinutes - 6 * 60) / totalMinutes) * 100;
  const width = Math.max(18, ((endMinutes - startMinutes) / totalMinutes) * 100);
  return {
    left: `${Math.min(94, left)}%`,
    width: `max(${Math.min(width, 96 - left)}%, 118px)`,
  };
}

function getDriverTimeOffItemsForDay(driver, dateKey, driverTimeOffByDate = {}) {
  const driverId = driver?.id || driver?.driver_id || "";
  const driverName = getMobileDriverName(driver);
  return (driverTimeOffByDate?.[dateKey] || []).filter((item) => (
    (driverId && item?.driver_id === driverId) ||
    (!driverId && item?.driver_name && item.driver_name === driverName)
  ));
}

function getMobileTimeOffLabel(item) {
  if (!item) return "请假";
  const reason = item.reason ? ` · ${item.reason}` : "";
  if (item.all_day !== false) return `全天请假${reason}`;
  return `${item.start_time || "--:--"}-${item.end_time || "--:--"} 请假${reason}`;
}

function AdminMobileDispatchTimelineCard({
  driver,
  orders,
  days,
  driverTimeOffByDate = {},
  viewHelpers,
  onOpenDriverHistory,
  onOpenReservationDetail,
}) {
  return (
    <article className="admin-mobile-dispatch-timeline-card" data-testid="admin-mobile-dispatch-timeline-card">
      <header>
        <button
          type="button"
          className="admin-mobile-dispatch-driver-history-button"
          onClick={() => onOpenDriverHistory?.(driver)}
          aria-label={`查看${getMobileDriverName(driver)}的全部预约`}
        >
          <span className="admin-mobile-driver-avatar">
            <UserRound className="h-5 w-5" />
          </span>
          <span className="admin-mobile-dispatch-driver-history-copy">
            <strong>{getMobileDriverName(driver)}</strong>
            <small>{getMobileDriverContact(driver)} · 排班 {orders.length} 单</small>
          </span>
          <span className="admin-mobile-dispatch-driver-history-entry">
            全部预约
            <ChevronRight className="h-4 w-4" />
          </span>
        </button>
      </header>
      <div className="admin-mobile-dispatch-timeline-days">
        {days.map((day) => {
          const dayOrders = orders.filter((order) => getMobileDispatchDateKey(order) === day.dateKey);
          const timeOffItems = getDriverTimeOffItemsForDay(driver, day.dateKey, driverTimeOffByDate);
          return (
            <section key={`${driver.id}-${day.dateKey}`} className="admin-mobile-dispatch-day-row">
              <div className="admin-mobile-dispatch-day-label">
                <strong>{day.label}</strong>
                <span>{day.dateKey.slice(5).replace("-", "/")}</span>
              </div>
              {timeOffItems.length > 0 && (
                <div className="admin-mobile-dispatch-time-off-list" aria-label="司机请假">
                  {timeOffItems.map((item) => (
                    <div key={item.id || `${item.driver_id}-${day.dateKey}-${item.start_time || "all"}`} className="admin-mobile-dispatch-time-off">
                      <Clock3 className="h-3.5 w-3.5" />
                      <span>{getMobileTimeOffLabel(item)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="admin-mobile-dispatch-time-wrap">
                <div className="admin-mobile-dispatch-time-labels">
                  {MOBILE_DISPATCH_TIME_AXIS_HOURS.map((hour) => <span key={hour}>{hour}</span>)}
                </div>
                <div className="admin-mobile-dispatch-time-axis">
                  {dayOrders.length === 0 ? (
                    <div className={timeOffItems.length > 0 ? "admin-mobile-dispatch-free is-time-off" : "admin-mobile-dispatch-free"}>
                      {timeOffItems.length > 0 ? "请假中" : "空き"}
                    </div>
                  ) : dayOrders.map((order, index) => {
                    const isExtraSchedule = isMobileDispatchExtraSchedule(order);
                    const blockClassName = [
                      "admin-mobile-dispatch-timeline-block",
                      isExtraSchedule ? "is-extra-schedule" : "",
                      order?.source_type === "driver_extra" ? "is-driver-extra" : "",
                    ].filter(Boolean).join(" ");
                    const blockStyle = {
                      ...getMobileDispatchBlockStyle(order),
                      top: `${6 + (index % 2) * 22}px`,
                    };
                    const blockContent = (
                      <>
                        <span>{getMobileDispatchTime(order)} {getMobileOrderCustomer(order)}</span>
                        <small>{isExtraSchedule ? `${getMobileDispatchSourceLabel(order)} · ` : ""}{getMobileRoute(order, viewHelpers)}</small>
                      </>
                    );

                    return (
                      <button
                        key={order.id || order.order_no || `${order.driver_id}-${order.date}-${order.time}-${index}`}
                        type="button"
                        className={blockClassName}
                        style={blockStyle}
                        title={`${getMobileDispatchSourceLabel(order)} · 点击查看详情`}
                        onClick={() => onOpenReservationDetail?.(order)}
                      >
                        {blockContent}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </article>
  );
}

function getMobileDispatchProgressCategory(item) {
  const status = getOrderStatus(item);
  if (["cancelled", "deleted", "refunded"].includes(status)) return "cancelled";
  if (status === "completed") return "completed";
  if (!isMobileDispatchExtraSchedule(item)) {
    return getMobileOrderTimeCategory(item) === "completed" ? "completed" : "unfinished";
  }

  const dateKey = getMobileDispatchDateKey(item);
  const todayKey = getJapanDateKey(0);
  if (!dateKey || dateKey > todayKey) return "unfinished";
  if (dateKey < todayKey) return "completed";

  const nowParts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const nowHour = Number(nowParts.find((part) => part.type === "hour")?.value || 0);
  const nowMinute = Number(nowParts.find((part) => part.type === "minute")?.value || 0);
  const reservationMinutes = parseClockMinutes(getMobileDispatchTime(item));
  if (reservationMinutes === null) return "unfinished";
  return reservationMinutes <= nowHour * 60 + nowMinute ? "completed" : "unfinished";
}

function getMobileDispatchProgressLabel(item) {
  const labels = {
    unfinished: "未完成",
    completed: "已完成",
    cancelled: "已取消",
  };
  return labels[getMobileDispatchProgressCategory(item)] || "状态未设置";
}

function sortMobileDispatchHistoryItems(items = []) {
  return [...items].sort((a, b) => {
    const leftCategory = getMobileDispatchProgressCategory(a);
    const rightCategory = getMobileDispatchProgressCategory(b);
    const categoryRank = { unfinished: 0, completed: 1, cancelled: 2 };
    if (categoryRank[leftCategory] !== categoryRank[rightCategory]) {
      return categoryRank[leftCategory] - categoryRank[rightCategory];
    }
    const left = `${getMobileDispatchDateKey(a) || "1900-01-01"} ${getMobileDispatchTime(a) || "00:00"}`;
    const right = `${getMobileDispatchDateKey(b) || "1900-01-01"} ${getMobileDispatchTime(b) || "00:00"}`;
    return leftCategory === "unfinished" ? left.localeCompare(right) : right.localeCompare(left);
  });
}

function getMobileDispatchAmountLabel(item) {
  const amount = item?.price_amount ?? item?.calculated_price ?? item?.price ?? item?.total_amount;
  const number = Number(amount);
  if (!Number.isFinite(number)) return "未设置";
  const currency = item?.price_currency || "JPY";
  return currency === "JPY" ? `¥${number.toLocaleString("ja-JP")}` : `${number.toLocaleString()} ${currency}`;
}

export function AdminMobileDispatchHistoryPage({
  driver,
  items = [],
  loading = false,
  error = "",
  viewHelpers,
  onBack,
  onOpenReservationDetail,
  onRetry,
}) {
  const [filter, setFilter] = React.useState("all");
  const [viewMode, setViewMode] = React.useState("list");
  const counts = React.useMemo(() => ({
    all: items.length,
    unfinished: items.filter((item) => getMobileDispatchProgressCategory(item) === "unfinished").length,
    completed: items.filter((item) => getMobileDispatchProgressCategory(item) === "completed").length,
    cancelled: items.filter((item) => getMobileDispatchProgressCategory(item) === "cancelled").length,
  }), [items]);
  const visibleItems = React.useMemo(() => sortMobileDispatchHistoryItems(
    filter === "all" ? items : items.filter((item) => getMobileDispatchProgressCategory(item) === filter),
  ), [filter, items]);
  const filters = [
    ["all", "全部"],
    ["unfinished", "未完成"],
    ["completed", "已完成"],
    ["cancelled", "已取消"],
  ];

  return (
    <div className="admin-mobile-home admin-mobile-dispatch-history-page" data-testid="admin-mobile-dispatch-history-page">
      <header className="admin-mobile-dispatch-subpage-header">
        <button type="button" onClick={onBack} aria-label="返回调度时间轴">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <span>Driver Schedule</span>
          <h1>{getMobileDriverName(driver)}</h1>
          <p>全部预约记录，包含未完成和已完成</p>
        </div>
      </header>

      <section className="admin-mobile-dispatch-history-metrics" aria-label="预约统计">
        <div><strong>{counts.all}</strong><span>全部</span></div>
        <div><strong>{counts.unfinished}</strong><span>未完成</span></div>
        <div><strong>{counts.completed}</strong><span>已完成</span></div>
      </section>

      <div className="admin-mobile-dispatch-history-filters" role="tablist" aria-label="预约状态筛选">
        {filters.map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={filter === value ? "is-active" : ""}
            onClick={() => setFilter(value)}
          >
            {label} {counts[value]}
          </button>
        ))}
      </div>

      <div className="admin-mobile-dispatch-history-filters" role="group" aria-label="排班显示方式">
        {[["list", "列表"], ["chart", "图表"]].map(([value, label]) => (
          <button key={value} type="button" aria-pressed={viewMode === value} className={viewMode === value ? "is-active" : ""} onClick={() => setViewMode(value)}>{label}</button>
        ))}
      </div>

      {loading ? (
        <p className="admin-mobile-empty">正在加载全部预约...</p>
      ) : error ? (
        <section className="admin-mobile-dispatch-history-error">
          <p>{error}</p>
          <button type="button" onClick={onRetry}>重新加载</button>
        </section>
      ) : visibleItems.length === 0 ? (
        <p className="admin-mobile-empty">暂无符合条件的预约</p>
      ) : (
        viewMode === "chart" ? (
          <section className="admin-mobile-dispatch-timeline-list" data-testid="admin-mobile-dispatch-history-chart">
            {[...new Set(visibleItems.map(getMobileDispatchDateKey))].map(dateKey => (
              <AdminMobileDispatchTimelineCard key={dateKey || "unknown"} driver={driver}
                days={[{ dateKey, label: dateKey || "日期待确认" }]}
                orders={visibleItems.filter(item => getMobileDispatchDateKey(item) === dateKey)}
                viewHelpers={viewHelpers} onOpenReservationDetail={onOpenReservationDetail} />
            ))}
          </section>
        ) : <section className="admin-mobile-dispatch-history-list">
          {visibleItems.map((item, index) => (
            <button
              key={item.id || item.order_no || `${getMobileDispatchDateKey(item)}-${getMobileDispatchTime(item)}-${index}`}
              type="button"
              className="admin-mobile-dispatch-history-card"
              onClick={() => onOpenReservationDetail?.(item)}
            >
              <span className={`admin-mobile-dispatch-history-status is-${getMobileDispatchProgressCategory(item)}`}>
                {getMobileDispatchProgressLabel(item)}
              </span>
              <span className="admin-mobile-dispatch-history-date">
                {getMobileDispatchDateKey(item) || "日期未设置"} · {getMobileDispatchTime(item)}
              </span>
              <strong>{getMobileOrderCustomer(item)}</strong>
              <p>{getMobileRoute(item, viewHelpers)}</p>
              <small>{getMobileDispatchSourceLabel(item)} · {item.order_no || item.id || "无编号"}</small>
              <ChevronRight className="h-5 w-5" />
            </button>
          ))}
        </section>
      )}
    </div>
  );
}

function AdminMobileDispatchReservationDetailPage({
  item,
  driver,
  viewHelpers,
  onBack,
  onOpenOfficialOrder,
}) {
  const isExtraSchedule = isMobileDispatchExtraSchedule(item);
  const vehicleName = viewHelpers?.getVehicleTypeName?.(item?.vehicle_type) || item?.vehicle_type || "车型未设置";
  const rows = [
    ["预约来源", getMobileDispatchSourceLabel(item)],
    ["预约进度", getMobileDispatchProgressLabel(item)],
    ["订单状态", isExtraSchedule ? (item?.status === "cancelled" ? "已取消" : "有效") : getMobileOrderStatusLabel(item)],
    ["司机", getMobileDriverName(driver) || getAssignedDriverName(item)],
    ["车型", vehicleName],
    ["人数 / 行李", formatPassengerLuggage(item)],
    ["航班", item?.flight_number || "未设置"],
    ["金额", getMobileDispatchAmountLabel(item)],
    ["订单号", item?.order_no || item?.id || "未设置"],
    ["创建人", item?.created_by_name || (isExtraSchedule ? "司机本人" : "系统订单")],
  ];

  return (
    <div className="admin-mobile-home admin-mobile-dispatch-reservation-detail" data-testid="admin-mobile-dispatch-reservation-detail">
      <header className="admin-mobile-dispatch-reservation-hero">
        <div className="admin-mobile-dispatch-reservation-topline">
          <button type="button" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            返回
          </button>
          <span className={`is-${getMobileDispatchProgressCategory(item)}`}>{getMobileDispatchProgressLabel(item)}</span>
        </div>
        <time>{getMobileDispatchDateKey(item) || "日期未设置"} · {getMobileDispatchTime(item)}</time>
        <h1>{getMobileOrderCustomer(item)}</h1>
        <p>{getMobileRoute(item, viewHelpers)}</p>
      </header>

      <section className="admin-mobile-dispatch-reservation-grid">
        {rows.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{formatMobileDetailValue(value)}</strong>
          </div>
        ))}
      </section>

      <section className="admin-mobile-order-detail-card">
        <span>上车地点</span>
        <p>{formatMobileDetailValue(item?.pickup_location_detail || item?.pickup_location || item?.pickup_area, "未设置")}</p>
      </section>
      <section className="admin-mobile-order-detail-card">
        <span>下车地点</span>
        <p>{formatMobileDetailValue(item?.dropoff_location_detail || item?.dropoff_location || item?.dropoff_area || item?.destination, "未设置")}</p>
      </section>
      <section className="admin-mobile-order-detail-card">
        <span>客户联系方式</span>
        <p>{formatMobileDetailValue(item?.customer_contact || item?.customer_phone || item?.phone || item?.customer_email, "未设置")}</p>
      </section>
      <section className="admin-mobile-order-detail-card">
        <span>备注</span>
        <p>{formatMobileDetailValue(item?.notes || item?.trip_details || item?.dispatch_note, "暂无备注")}</p>
      </section>

      {!isExtraSchedule && (
        <button type="button" className="admin-mobile-dispatch-open-official" onClick={() => onOpenOfficialOrder?.(item)}>
          打开完整订单和操作
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

function AdminMobileDispatchPage({
  orders = [],
  extraSchedules = [],
  extraSchedulesLoading = false,
  extraSchedulesError = "",
  driverAccounts = [],
  driverAccountsError,
  driverAccountsLoading,
  onRetryDrivers,
  driverTimeOffByDate = {},
  selectedDriverIds = [],
  hasInitializedDriverSelection = false,
  viewHelpers,
  onFetchDriverHistory,
  onOpenOrderDetail,
  onSelectedDriverIdsChange,
  onDriverSelectionInitializedChange,
  onRefreshData,
  onSelectAdminTab,
}) {
  const activeDrivers = React.useMemo(
    () => driverAccounts.filter((driver) => driver?.is_active !== false),
    [driverAccounts],
  );
  const [showDriverPicker, setShowDriverPicker] = React.useState(false);
  const [selectedHistoryDriver, setSelectedHistoryDriver] = React.useState(null);
  const [driverHistoryItems, setDriverHistoryItems] = React.useState([]);
  const [driverHistoryLoading, setDriverHistoryLoading] = React.useState(false);
  const [driverHistoryError, setDriverHistoryError] = React.useState("");
  const [selectedReservation, setSelectedReservation] = React.useState(null);

  React.useEffect(() => {
    if (!hasInitializedDriverSelection && activeDrivers.length > 0) {
      onSelectedDriverIdsChange?.(activeDrivers.slice(0, 4).map((driver) => driver.id).filter(Boolean));
      onDriverSelectionInitializedChange?.(true);
    }
  }, [activeDrivers, hasInitializedDriverSelection, onDriverSelectionInitializedChange, onSelectedDriverIdsChange]);

  React.useEffect(() => {
    if (!hasInitializedDriverSelection || driverAccountsLoading || driverAccountsError || activeDrivers.length === 0) return;
    const activeDriverIds = new Set(activeDrivers.map((driver) => driver.id).filter(Boolean));
    onSelectedDriverIdsChange?.((current) => current.filter((driverId) => activeDriverIds.has(driverId)));
  }, [activeDrivers, hasInitializedDriverSelection, driverAccountsLoading, driverAccountsError, onSelectedDriverIdsChange]);

  const selectedDriverSet = React.useMemo(() => new Set(selectedDriverIds), [selectedDriverIds]);
  const dispatchOrders = React.useMemo(() => {
    const officialOrders = orders.filter((order) => {
      const driverId = getOrderAssignedDriverId(order);
      return driverId && selectedDriverSet.has(driverId);
    });
    const sharedExtraSchedules = extraSchedules.filter((schedule) => {
      const driverId = getOrderAssignedDriverId(schedule);
      return schedule?.status !== "cancelled" && driverId && selectedDriverSet.has(driverId);
    });
    return sortMobileDispatchItems([...officialOrders, ...sharedExtraSchedules]);
  }, [extraSchedules, orders, selectedDriverSet]);
  const visibleExtraScheduleCount = React.useMemo(
    () => dispatchOrders.filter(isMobileDispatchExtraSchedule).length,
    [dispatchOrders],
  );
  const dispatchDays = React.useMemo(() => getMobileDispatchDays(), []);

  const toggleDriver = (driverId) => {
    if (!driverId) return;
    onSelectedDriverIdsChange?.((current) => (
      current.includes(driverId)
        ? current.filter((id) => id !== driverId)
        : [...current, driverId]
    ));
  };

  const loadDriverHistory = async (driver) => {
    if (!driver?.id) return;
    setDriverHistoryLoading(true);
    setDriverHistoryError("");
    try {
      const data = await onFetchDriverHistory?.(driver.id);
      setDriverHistoryItems([
        ...(Array.isArray(data?.official_orders) ? data.official_orders : []),
        ...(Array.isArray(data?.extra_schedules) ? data.extra_schedules : []),
      ]);
    } catch (error) {
      console.error("获取司机全部预约失败:", error);
      setDriverHistoryItems([]);
      setDriverHistoryError("全部预约加载失败，请重试");
    } finally {
      setDriverHistoryLoading(false);
    }
  };

  const openDriverHistory = (driver) => {
    setSelectedHistoryDriver(driver);
    setSelectedReservation(null);
    loadDriverHistory(driver);
  };

  if (selectedReservation) {
    return (
      <AdminMobileDispatchReservationDetailPage
        item={selectedReservation}
        driver={selectedHistoryDriver || activeDrivers.find((driver) => driver.id === getOrderAssignedDriverId(selectedReservation))}
        viewHelpers={viewHelpers}
        onBack={() => setSelectedReservation(null)}
        onOpenOfficialOrder={onOpenOrderDetail}
      />
    );
  }

  if (selectedHistoryDriver) {
    return (
      <AdminMobileDispatchHistoryPage
        driver={selectedHistoryDriver}
        items={driverHistoryItems}
        loading={driverHistoryLoading}
        error={driverHistoryError}
        viewHelpers={viewHelpers}
        onBack={() => setSelectedHistoryDriver(null)}
        onOpenReservationDetail={setSelectedReservation}
        onRetry={() => loadDriverHistory(selectedHistoryDriver)}
      />
    );
  }

  return (
    <div className="admin-mobile-home" data-testid="admin-mobile-dispatch-page">
      <header className="admin-mobile-page-header">
        <div>
          <span>Dispatch</span>
          <h1>司机调度</h1>
          <p>显示正式订单、全部调度预约和司机追加。</p>
        </div>
        <button type="button" onClick={onRefreshData} aria-label="刷新调度预约" title="刷新调度预约">
          <RefreshCw className={`h-5 w-5 ${extraSchedulesLoading ? "animate-spin" : ""}`} />
        </button>
      </header>

      {driverAccountsError && <section role="alert" className="admin-mobile-dispatch-error"><p>司机资料加载失败：{driverAccountsError}。当前列表不代表没有司机。</p><button type="button" onClick={onRetryDrivers}>重新加载司机</button></section>}
      {driverAccountsLoading && <p role="status">正在加载司机资料…</p>}
      {extraSchedulesError && <p className="admin-mobile-dispatch-error" role="alert">{extraSchedulesError}</p>}

      <section className="admin-mobile-dispatch-driver-filter" aria-label="选择司机">
        <div className="admin-mobile-section-title">
          <div>
            <h2>选择司机</h2>
            <p>已选 {selectedDriverIds.length} / {activeDrivers.length}</p>
          </div>
          <button type="button" onClick={() => setShowDriverPicker(true)}>
            打开选择
          </button>
        </div>
        <div className="admin-mobile-dispatch-selected-drivers">
          {selectedDriverIds.length === 0 ? "未选择司机" : activeDrivers
            .filter((driver) => selectedDriverSet.has(driver.id))
            .slice(0, 4)
            .map(getMobileDriverName)
            .join("、")}
          {selectedDriverIds.length > 4 ? ` 等 ${selectedDriverIds.length} 人` : ""}
        </div>
      </section>

      {showDriverPicker && (
        <div className="admin-mobile-dispatch-picker-backdrop" role="dialog" aria-modal="true">
          <section className="admin-mobile-dispatch-picker">
            <header>
              <div>
                <span>Driver Filter</span>
                <h2>选择司机</h2>
              </div>
              <button type="button" onClick={() => setShowDriverPicker(false)}>完成</button>
            </header>
            <div className="admin-mobile-dispatch-picker-actions">
              <button type="button" onClick={() => onSelectedDriverIdsChange?.(activeDrivers.map((driver) => driver.id).filter(Boolean))}>全选</button>
              <button type="button" onClick={() => onSelectedDriverIdsChange?.([])}>清空</button>
            </div>
            <div className="admin-mobile-dispatch-picker-list">
              {activeDrivers.length === 0 ? (
                <p className="admin-mobile-empty">{driverAccountsError ? "司机资料加载失败，请重试" : driverAccountsLoading ? "正在加载司机…" : "暂无可用司机"}</p>
              ) : activeDrivers.map((driver) => (
                <label key={driver.id || driver.phone || getMobileDriverName(driver)} className="admin-mobile-dispatch-picker-row">
                  <span>
                    <strong>{getMobileDriverName(driver)}</strong>
                    <small>{getMobileDriverContact(driver)}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={selectedDriverSet.has(driver.id)}
                    onChange={() => toggleDriver(driver.id)}
                  />
                </label>
              ))}
            </div>
          </section>
        </div>
      )}

      <section className="admin-mobile-section">
        <div className="admin-mobile-section-title">
          <div>
            <h2>时间轴</h2>
            <p>正式订单 {dispatchOrders.length - visibleExtraScheduleCount} · 追加预约 {visibleExtraScheduleCount}</p>
          </div>
          <span>{dispatchOrders.length}</span>
        </div>
        <div className="admin-mobile-dispatch-timeline-list">
          {selectedDriverIds.length === 0 ? (
            <p className="admin-mobile-empty">请先选择司机</p>
          ) : activeDrivers
            .filter((driver) => selectedDriverSet.has(driver.id))
            .map((driver) => (
              <AdminMobileDispatchTimelineCard
                key={driver.id || driver.phone || getMobileDriverName(driver)}
                driver={driver}
                days={dispatchDays}
                orders={dispatchOrders.filter((order) => getOrderAssignedDriverId(order) === driver.id)}
                driverTimeOffByDate={driverTimeOffByDate}
                viewHelpers={viewHelpers}
                onOpenDriverHistory={openDriverHistory}
                onOpenReservationDetail={setSelectedReservation}
              />
            ))}
        </div>
      </section>
    </div>
  );
}

function AdminMobileOrderDetailPage({
  order,
  viewHelpers,
  onAssignDriver,
  onDuplicateOrder,
  onDeleteOrder,
  onDownloadSignboard,
  onEditOrder,
  onCreateDriverComplaint,
  onGeneratePaymentPage,
  onOpenTripCommunication,
  onSettleOrder,
  onSelectAdminTab,
}) {
  if (!order) {
    return (
      <div className="admin-mobile-home admin-mobile-order-detail-page" data-testid="admin-mobile-order-detail-page">
        <header className="admin-mobile-page-header">
          <div>
            <span>Order Detail</span>
            <h1>订单详情</h1>
            <p>请选择一个订单</p>
          </div>
          <button type="button" onClick={() => onSelectAdminTab?.("orders")}>返回</button>
        </header>
      </div>
    );
  }

  const assignedDriver = getAssignedDriverName(order);
  const vehicleName = viewHelpers?.getVehicleTypeName?.(order.vehicle_type) || order.vehicle_type || "车型未填";
  const route = getMobileRoute(order, viewHelpers);
  const rows = [
    ["订单号", order.order_no || order.confirmation_code || order.id || "-"],
    ["日期", getMobileOrderDateLabel(order)],
    ["时间", order.pickup_time || "--:--"],
    ["客户", getMobileOrderCustomer(order)],
    ["客户代码", getMobileOrderCustomerCode(order)],
    ["关联账户", getMobileOrderLinkedAccount(order)],
    ["电话", order.phone || "-"],
    ["状态", getMobileOrderStatusLabel(order)],
    ["车型", vehicleName],
    ...(order.service_type === "charter" ? [["包车时长", `${order.selected_hours || "-"} 小时`]] : []),
    ["人数 / 行李", formatPassengerLuggage(order)],
    ["司机", assignedDriver || "未配车"],
    ["航班", order.flight_number || "-"],
  ];

  return (
    <div className="admin-mobile-home admin-mobile-order-detail-page" data-testid="admin-mobile-order-detail-page">
      <header className="admin-mobile-order-detail-hero">
        <div className="admin-mobile-order-detail-topline">
          <button type="button" onClick={() => onSelectAdminTab?.("orders")}>返回</button>
          <span>{getMobileOrderStatusLabel(order)}</span>
        </div>
        <time>{getMobileOrderDateLabel(order)} · {order.pickup_time || "--:--"}</time>
        <h1>{getMobileOrderCustomer(order)}</h1>
        <p>{route}</p>
      </header>

      <section className="admin-mobile-order-detail-actions" aria-label="订单操作">
        <button type="button" onClick={() => onEditOrder?.(order)}>
          <Pencil className="h-4 w-4" />
          编辑
        </button>
        <button type="button" onClick={() => onAssignDriver?.(order)}>
          <UserPlus className="h-4 w-4" />
          配车
        </button>
        <button type="button" onClick={(event) => handleMobileOrderAction(event, onCreateDriverComplaint, order)}>
          <AlertTriangle className="h-4 w-4" />
          投诉
        </button>
        <button type="button" onClick={(event) => handleMobilePaymentAction(event, onGeneratePaymentPage, order)}>
          <CreditCard className="h-4 w-4" />
          付款
        </button>
        <button type="button" onClick={() => onOpenTripCommunication?.(order)}>
          <MessageCircle className="h-4 w-4" />
          沟通
        </button>
        {canOpenSettlement(order) && (
          <button
            type="button"
            onClick={() => onSettleOrder?.(order)}
            disabled={["pending", "paid"].includes(order?.driver_settlement_status)}
          >
            <WalletCards className="h-4 w-4" />
            {getSettlementActionLabel(order)}
          </button>
        )}
        <TransportAcceptance order={order} admin />
        <button type="button" onClick={() => onDownloadSignboard?.(order)}>
          <Download className="h-4 w-4" />
          举牌
        </button>
        <button type="button" onClick={() => onDuplicateOrder?.(order)}>
          <Copy className="h-4 w-4" />
          复制
        </button>
        <button type="button" onClick={() => exportOrderInfoJapanese(order)}>
          <FileText className="h-4 w-4" />
          文本
        </button>
        <button type="button" onClick={(event) => handleMobileDeleteOrderAction(event, onDeleteOrder, order)}>
          <Trash2 className="h-4 w-4" />
          删除
        </button>
      </section>

      <section className="admin-mobile-order-detail-grid">
        {rows.map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{formatMobileDetailValue(value)}</strong>
          </div>
        ))}
      </section>

      <section className="admin-mobile-order-detail-card">
        <span>上车地点</span>
        <p>{formatMobileDetailValue(viewHelpers?.formatPickupDisplay?.(order) || order.pickup_location_detail || order.pickup_location || order.pickup_area)}</p>
      </section>

      <section className="admin-mobile-order-detail-card">
        <span>下车地点</span>
        <p>{formatMobileDetailValue(viewHelpers?.formatDestinationDisplay?.(order) || order.dropoff_location_detail || order.destination || order.dropoff_area)}</p>
      </section>

      <section className="admin-mobile-order-detail-card">
        <span>备注</span>
        <p>{formatMobileDetailValue(order.trip_details || order.dispatch_note, "暂无备注")}</p>
      </section>

      {shouldShowMobilePaymentStatus(order) && (
        <section className={`admin-mobile-order-detail-card admin-mobile-order-payment-status ${getMobilePaymentStatusClass(order)}`}>
          <span>付款状态</span>
          <p>{getMobilePaymentStatusLabel(order)}</p>
          {(order.square_payment_link_id || order.square_payment_id) && (
            <small>{order.square_payment_link_id || order.square_payment_id}</small>
          )}
        </section>
      )}
    </div>
  );
}

function AdminMobileDriversPage({ driverAccountsError, driverAccountsLoading, onRetryDrivers, driverAccounts = [], onOpenCreateDriver, onOpenEditDriver, onSelectAdminTab }) {
  const activeDrivers = driverAccounts.filter((driver) => driver?.is_active !== false);
  const inactiveDrivers = driverAccounts.length - activeDrivers.length;
  const visibleDrivers = [
    ...activeDrivers,
    ...driverAccounts.filter((driver) => driver?.is_active === false),
  ];
  return (
    <div className="admin-mobile-home" data-testid="admin-mobile-drivers-page">
      <header className="admin-mobile-driver-hero">
        <div className="admin-mobile-driver-hero-top">
          <span className="admin-mobile-driver-badge">Drivers</span>
        </div>
        <h1>司机管理</h1>
        <p>快速查看司机状态，添加账号和管理资料都在手机里完成。</p>
      </header>

      {driverAccountsError && <section role="alert" className="admin-mobile-dispatch-error"><p>司机资料加载失败：{driverAccountsError}。当前列表不代表没有司机。</p><button type="button" onClick={onRetryDrivers}>重新加载司机</button></section>}
      {driverAccountsLoading && <p role="status">正在加载司机资料…</p>}
      <section className="admin-mobile-driver-metrics" aria-label="司机状态">
        <div>
          <span>{driverAccounts.length}</span>
          <p>全部司机</p>
        </div>
        <div>
          <span>{activeDrivers.length}</span>
          <p>启用</p>
        </div>
        <div>
          <span>{inactiveDrivers}</span>
          <p>停用</p>
        </div>
      </section>

      <section className="admin-mobile-driver-actions" aria-label="司机快捷操作">
        <button
          type="button"
          className="admin-mobile-driver-primary-action"
          onClick={() => {
            onOpenCreateDriver?.();
            onSelectAdminTab?.("mobileDriverManage");
          }}
        >
          <span className="admin-mobile-driver-action-icon">
            <UserPlus className="h-5 w-5" />
          </span>
          <span>
            <strong>添加司机</strong>
            <small>创建账号、手机号和登录权限</small>
          </span>
        </button>
      </section>

      <section className="admin-mobile-section admin-mobile-driver-detail">
        <div className="admin-mobile-section-title">
          <div>
            <h2>司机列表</h2>
            <p>按启用状态优先显示</p>
          </div>
          <span>{visibleDrivers.length}</span>
        </div>
        <div className="admin-mobile-driver-list">
          {driverAccounts.length === 0 ? (
            <p className="admin-mobile-empty">{driverAccountsError ? "司机资料加载失败，请重试" : driverAccountsLoading ? "正在加载司机…" : "暂无司机账号"}</p>
          ) : visibleDrivers.map((driver) => (
            <article
              key={driver.id || driver.email || getMobileDriverName(driver)}
              className="admin-mobile-driver-card admin-mobile-driver-card-shell"
            >
              <span className="admin-mobile-driver-avatar">
                <UserRound className="h-5 w-5" />
              </span>
              <span className="admin-mobile-driver-copy">
                <strong>{getMobileDriverName(driver)}</strong>
                <small>{getMobileDriverContact(driver)}</small>
                <em>{getMobileDriverVehicle(driver)} · {getMobileDriverArea(driver)}</em>
              </span>
              <span className={driver?.is_active === false ? "is-off" : "is-on"}>
                {driver?.is_active === false ? "停用" : "启用"}
              </span>
              <button
                type="button"
                className="admin-mobile-driver-manage"
                onClick={() => {
                  onOpenEditDriver?.(driver);
                  onSelectAdminTab?.("mobileDriverManage");
                }}
              >
                管理
              </button>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function AdminMobileDriverManagePage({
  editingDriverAccount,
  driverAccountForm,
  onDriverAccountFormChange,
  onCreateDriverAccount,
  onUpdateDriverAccount,
  onSelectAdminTab,
}) {
  const isEditing = Boolean(editingDriverAccount);
  const updateField = (field, value) => {
    onDriverAccountFormChange?.({
      ...driverAccountForm,
      [field]: value,
    });
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!driverAccountForm.name || !driverAccountForm.phone || (!isEditing && !driverAccountForm.password)) {
      alert("请填写完整信息");
      return;
    }
    if (!isEditing && String(driverAccountForm.password || "").length < 6) {
      alert("密码至少需要6个字符");
      return;
    }
    if (isEditing) {
      await onUpdateDriverAccount?.();
    } else {
      await onCreateDriverAccount?.();
    }
    onSelectAdminTab?.("drivers");
  };

  return (
    <div className="admin-mobile-home" data-testid="admin-mobile-driver-manage-page">
      <header className="admin-mobile-page-header">
        <div>
          <span>{isEditing ? "Manage Driver" : "New Driver"}</span>
          <h1>{isEditing ? "管理司机" : "添加司机"}</h1>
          <p>手机端只保留常用账号字段和权限开关，保存后仍使用原后台数据。</p>
        </div>
        <button type="button" onClick={() => onSelectAdminTab?.("drivers")}>
          返回
        </button>
      </header>

      <form className="admin-mobile-driver-form" onSubmit={handleSubmit}>
        <section className="admin-mobile-create-card" data-mobile-section="账号">
          <label>
            <span>司机姓名</span>
            <input
              type="text"
              value={driverAccountForm.name || ""}
              onChange={(event) => updateField("name", event.target.value)}
              placeholder="输入司机姓名"
            />
          </label>
          <label>
            <span>手机号</span>
            <input
              type="tel"
              value={driverAccountForm.phone || ""}
              onChange={(event) => updateField("phone", event.target.value)}
              placeholder="输入登录手机号"
            />
          </label>
          {!isEditing && (
            <label>
              <span>初始密码</span>
              <input
                type="password"
                value={driverAccountForm.password || ""}
                onChange={(event) => updateField("password", event.target.value)}
                placeholder="至少 6 位"
              />
            </label>
          )}
        </section>

        <section className="admin-mobile-create-card admin-mobile-driver-toggle-card" data-mobile-section="权限">
          {[
            ["can_view_revenue_stats", "允许查看收入统计"],
            ["include_in_internal_dispatch_overview", "进入内部派单概览"],
            ["can_request_leave", "允许申请休假"],
            ["is_vip", "重点司机标记"],
          ].map(([field, label]) => (
            <label key={field} className="admin-mobile-driver-toggle">
              <span>{label}</span>
              <input
                type="checkbox"
                checked={Boolean(driverAccountForm[field])}
                onChange={(event) => updateField(field, event.target.checked)}
              />
            </label>
          ))}
        </section>

        <div className="admin-mobile-driver-submit">
          <button type="button" onClick={() => onSelectAdminTab?.("drivers")}>
            取消
          </button>
          <button type="submit">
            {isEditing ? "保存修改" : "创建司机"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AdminMobileCreatePage({ allUsers, areas, onOrderCreated, onSelectAdminTab }) {
  return (
    <LazyAdminSection>
      <AdminCreateOrderModal
        allUsers={allUsers}
        areas={areas}
        onClose={() => onSelectAdminTab?.("mobileHome")}
        onOrderCreated={onOrderCreated}
        presentation="mobile-page"
      />
    </LazyAdminSection>
  );
}

function AdminMobileCommunicationPage({ apiBaseUrl, communicationOrderId, communicationRequestNonce }) {
  return (
    <LazyAdminSection>
      <div className="admin-mobile-communication-page" data-testid="admin-mobile-communication-page">
        <TripCommunicationCenter
          apiBaseUrl={apiBaseUrl}
          initialOrderId={communicationOrderId}
          initialOrderRequestNonce={communicationRequestNonce}
          focusInitialOrder={Boolean(communicationOrderId)}
        />
      </div>
    </LazyAdminSection>
  );
}

function getMobileUserDisplayName(user) {
  return user?.company_name || user?.name || user?.email || "未命名客户";
}

function getMobileUserStatusLabel(status) {
  if (status === "approved") return "有效";
  if (status === "suspended") return "停止中";
  if (status === "rejected") return "已拒绝";
  if (status === "deleted") return "已删除";
  return "待审核";
}

function AdminMobileUsersPage({
  canViewAcceptance = false,
  pendingUsers = [],
  allUsers = [],
  loading,
  showDeletedUsers,
  selectedUserIds = [],
  onShowDeletedUsersChange,
  onApproveUser,
  onUpdateUserStatus,
  onToggleAllUsers,
  onToggleUserSelection,
  onBatchDeleteUsers,
  onOpenCreateUserForm,
  onOpenResetPassword,
  onOpenUserProfile,
  onOpenCustomerPricing,
  onUpdateUserChatSettings,
  onUpdateUserRetailMemberBindingSettings,
  onSelectAdminTab,
}) {
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [keyword, setKeyword] = React.useState("");
  const selectableUsers = allUsers.filter((user) => user.role !== "admin" && user.status !== "deleted");
  const visibleUsers = allUsers
    .filter((user) => user.role !== "admin")
    .filter((user) => showDeletedUsers || user.status !== "deleted")
    .filter((user) => statusFilter === "all" || user.status === statusFilter)
    .filter((user) => {
      const query = keyword.trim().toLowerCase();
      if (!query) return true;
      return [
        user.name,
        user.company_name,
        user.email,
        user.phone,
        user.customer_code,
        user.receipt_addressee,
      ].filter(Boolean).join(" ").toLowerCase().includes(query);
    });
  const approvedCount = allUsers.filter((user) => user.role !== "admin" && user.status === "approved").length;
  const suspendedCount = allUsers.filter((user) => user.role !== "admin" && user.status === "suspended").length;
  const deletedCount = allUsers.filter((user) => user.role !== "admin" && user.status === "deleted").length;

  return (
    <div className="admin-mobile-home admin-mobile-users-page" data-testid="admin-mobile-users-page">
      <header className="admin-mobile-page-header">
        <div>
          <span>Customers</span>
          <h1>客户管理</h1>
          <p>待审核、资料、权限、密码和专属价格都在这里处理。</p>
        </div>
        <button type="button" onClick={() => onSelectAdminTab?.("mobileHome")}>
          返回
        </button>
      </header>

      <section className="admin-mobile-user-metrics" aria-label="客户状态">
        <div>
          <span>{pendingUsers.length}</span>
          <p>待审核</p>
        </div>
        <div>
          <span>{approvedCount}</span>
          <p>有效</p>
        </div>
        <div>
          <span>{suspendedCount}</span>
          <p>停止</p>
        </div>
      </section>

      <section className="admin-mobile-user-toolbar" aria-label="客户管理操作">
        <label className="admin-mobile-search-field">
          <Search className="h-4 w-4" />
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索客户、邮箱、电话、客户编码"
          />
        </label>
        <div className="admin-mobile-user-actions">
          {canViewAcceptance && <TransportAcceptanceArchive
            triggerClassName="admin-mobile-user-acceptance"
            triggerLabel={<><FileText className="h-4 w-4" aria-hidden="true" /><span>运输承接书</span></>}
          />}
          <button type="button" onClick={onOpenCreateUserForm}>
            <UserPlus className="h-4 w-4" />
            新建账号
          </button>
          <button type="button" onClick={() => onShowDeletedUsersChange?.(!showDeletedUsers)}>
            {showDeletedUsers ? "隐藏已删" : `显示已删 ${deletedCount}`}
          </button>
        </div>
        {selectedUserIds.length > 0 && (
          <button type="button" className="admin-mobile-user-batch-delete" onClick={onBatchDeleteUsers} disabled={loading}>
            批量删除 {selectedUserIds.length}
          </button>
        )}
      </section>

      <section className="admin-mobile-segmented admin-mobile-user-tabs" aria-label="客户状态筛选">
        {[
          ["all", "全部", allUsers.filter((user) => user.role !== "admin" && (showDeletedUsers || user.status !== "deleted")).length],
          ["pending", "待审", pendingUsers.length],
          ["approved", "有效", approvedCount],
          ["suspended", "停止", suspendedCount],
        ].map(([value, label, count]) => (
          <button
            key={value}
            type="button"
            className={statusFilter === value ? "is-active" : ""}
            onClick={() => setStatusFilter(value)}
          >
            <span>{label}</span>
            <strong>{count}</strong>
          </button>
        ))}
      </section>

      {pendingUsers.length > 0 && (
        <section className="admin-mobile-section admin-mobile-user-review">
          <div className="admin-mobile-section-title">
            <div>
              <h2>待审核客户</h2>
              <p>审核后客户即可正常使用后台</p>
            </div>
            <span>{pendingUsers.length}</span>
          </div>
          <div className="admin-mobile-user-list">
            {pendingUsers.map((user) => (
              <article key={user.id || user.email} className="admin-mobile-user-card">
                <div className="admin-mobile-user-main">
                  <span className="admin-mobile-user-avatar"><UserRoundCog className="h-5 w-5" /></span>
                  <div>
                    <strong>{getMobileUserDisplayName(user)}</strong>
                    <small>{user.email || "邮箱未填"}</small>
                    <em>客户编码 {user.customer_code || "未设置"}</em>
                  </div>
                </div>
                <div className="admin-mobile-user-card-actions">
                  <button type="button" onClick={() => onApproveUser?.(user.id, "approved")} disabled={loading}>批准</button>
                  <button type="button" className="is-danger" onClick={() => onApproveUser?.(user.id, "rejected")} disabled={loading}>拒绝</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="admin-mobile-section">
        <div className="admin-mobile-section-title">
          <div>
            <h2>全部客户</h2>
            <p>选择、状态、密码、资料、价格与权限</p>
          </div>
          <button type="button" onClick={onToggleAllUsers}>
            全选 {selectableUsers.length}
          </button>
        </div>
        <div className="admin-mobile-user-list">
          {visibleUsers.length === 0 ? (
            <p className="admin-mobile-empty">没有匹配客户</p>
          ) : visibleUsers.map((user) => {
            const chatEnabled = user.chat_enabled !== false;
            const memberBindingEnabled = user.retail_member_order_binding_enabled !== false;
            const selected = selectedUserIds.includes(user.id);
            return (
              <article key={user.id || user.email} className={`admin-mobile-user-card ${selected ? "is-selected" : ""}`}>
                <div className="admin-mobile-user-main">
                  {user.status !== "deleted" && (
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleUserSelection?.(user.id)}
                    />
                  )}
                  {user.status === "deleted" && <span className="admin-mobile-user-select-spacer" />}
                  <span className="admin-mobile-user-avatar"><UserRoundCog className="h-5 w-5" /></span>
                  <div>
                    <strong>{getMobileUserDisplayName(user)}</strong>
                    <small>{user.email || "邮箱未填"}{user.phone ? ` · ${user.phone}` : ""}</small>
                    <em>客户编码 {user.customer_code || "未设置"}</em>
                  </div>
                  <span className={`admin-mobile-user-status is-${user.status || "pending"}`}>
                    {getMobileUserStatusLabel(user.status)}
                  </span>
                </div>

                <div className="admin-mobile-user-toggle-grid">
                  <button type="button" onClick={() => onUpdateUserChatSettings?.(user.id, !chatEnabled)} disabled={loading || user.status === "deleted"}>
                    聊天室 {chatEnabled ? "开" : "关"}
                  </button>
                  <button type="button" onClick={() => onUpdateUserRetailMemberBindingSettings?.(user.id, !memberBindingEnabled)} disabled={loading || user.status === "deleted"}>
                    订单绑定 {memberBindingEnabled ? "开" : "关"}
                  </button>
                </div>

                <div className="admin-mobile-user-card-actions">
                  {user.status !== "approved" && (
                    <button type="button" onClick={() => onUpdateUserStatus?.(user.id, "approved")} disabled={loading}>激活</button>
                  )}
                  {user.status === "approved" && (
                    <button type="button" onClick={() => onUpdateUserStatus?.(user.id, "suspended")} disabled={loading}>停止</button>
                  )}
                  <button type="button" onClick={() => onOpenResetPassword?.(user)}>改密</button>
                  <button type="button" onClick={() => onOpenUserProfile?.(user)}>资料</button>
                  <button type="button" onClick={() => onOpenCustomerPricing?.(user)}>价格</button>
                  {user.status !== "deleted" && (
                    <button type="button" className="is-danger" onClick={() => onUpdateUserStatus?.(user.id, "deleted")} disabled={loading}>删除</button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function AdminMobileAppSection({ title, eyebrow, description, onSelectAdminTab }) {
  return (
    <div className="admin-mobile-home" data-testid="admin-mobile-app-section">
      <header className="admin-mobile-page-header">
        <div>
          <span>{eyebrow}</span>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
      </header>
      <section className="admin-mobile-action-grid" aria-label="管理员 App 页面入口">
        <button type="button" onClick={() => onSelectAdminTab?.("orders")}>
          <CalendarDays className="h-5 w-5" />
          <span>订单</span>
        </button>
        <button type="button" onClick={() => onSelectAdminTab?.("drivers")}>
          <UserRound className="h-5 w-5" />
          <span>司机</span>
        </button>
        <button type="button" onClick={() => onSelectAdminTab?.("mobileHome")}>
          <ChevronRight className="h-5 w-5" />
          <span>首页</span>
        </button>
      </section>
    </div>
  );
}

function AdminMobileProfile({ onLogout }) {
  return (
    <div className="admin-mobile-home" data-testid="admin-mobile-profile">
      <header className="admin-mobile-header">
        <div className="admin-mobile-brand-lockup">
          <BrandLogoContainer className="admin-mobile-brand-mark" src={bigTaxiDriverLogo} alt="Big Taxi" />
          <div className="admin-mobile-brand-copy">
            <h1 className="driver-brand-title-gold">BIG TAXI</h1>
            <p className="driver-brand-subtitle-white">Private Transfer & Charter</p>
            <p className="driver-brand-page-meta">我的 · 管理员 App</p>
          </div>
        </div>
      </header>
      <section className="admin-mobile-section">
        <button type="button" className="admin-mobile-logout" onClick={onLogout}>
          <LogOut className="h-5 w-5" />
          退出登录
        </button>
      </section>
    </div>
  );
}

export default function AdminDashboardContent({
  onUsersChanged,
  activeTab,
  adminMobileOrderDetail,
  apiBaseUrl,
  allUsers,
  billingDocumentFilters,
  billingDocumentSummary,
  billingDocuments,
  billingDocumentsLoading,
  billingFilters,
  dashboardStats,
  dispatchers,
  dispatchersLoading,
  driverAccountForm,
  driverAccounts,
  driverAccountsError,
  driverAccountsLoading,
  fetchDriverAccounts,
  driverMonthlyStats,
  driverMonthlyStatsDispatcherId,
  driverMonthlyStatsDriverId,
  driverMonthlyStatsLoading,
  driverMonthlyStatsMonth,
  driverNoticeForm,
  driverNotices,
  driverNoticeTranslating,
  driverOnboardingLoading,
  driverTrainingConfig,
  driverTimeOffByDate,
  editingDriverAccount,
  editingDriverNotice,
  filteredOrders,
  communicationOrderId,
  communicationRequestNonce,
  selectedDriverOnboarding,
  settlementProps,
  user,
  handleApproveUser,
  handleBatchDeleteUsers,
  handleBulkDelete,
  handleBulkMarkPaid,
  isMarkingPaid,
  handleCreateDriverAccount,
  handleDeleteBillingDocument,
  handleDeleteDispatcher,
  handleDeleteDriverAccount,
  handleDriverMonthlyStatsDispatcherChange,
  handleDriverMonthlyStatsDriverChange,
  handleDriverMonthlyStatsMonthChange,
  handleSubmitDriverMonthlySettlements,
  handleUpdateDriverMonthlyDispatchPrice,
  handleDeleteOrder,
  handleDuplicateOrder,
  handleExportBilling,
  handleExportDriverMonthlyStats,
  handleApplyCustomerOvertimeCharge,
  handleMarkOvertimeHandled,
  handleMigrateDriverOrders,
  handleNotifyAssignedDriver,
  handlePutOrderIntoPool,
  handleWithdrawOrderFromPool,
  handleCreateDriverComplaint,
  handleResolveDriverIssue,
  handleReviewDriverOnboarding,
  handleEditDriverNotice,
  handleSaveDriverNotice,
  handleTranslateDriverNotice,
  handleSaveDriverTrainingConfig,
  handleOpenRecentOrder,
  handleResetDriverPassword,
  handleRestoreOrder,
  handleSearchKeyDown,
  handleSearchOrders,
  handleToggleDispatcherActive,
  handleToggleDriverActive,
  handleToggleDriverInternalDispatchOverview,
  handleToggleDriverPinned,
  handleToggleDriverRevenueStats,
  handleUnlockOrder,
  handleUpdateDriverAccount,
  handleUpdateUserChatSettings,
  handleUpdateUserRetailMemberBindingSettings,
  handleUpdateUserStatus,
  hideCancelled,
  hideCompleted,
  isOrderIncomplete,
  isAdminAppMode,
  isSearchMode,
  isSearching,
  layoutConfig,
  layoutLoading,
  loading,
  monthFilter,
  serviceDateRange,
  onServiceDateRangeChange,
  monthlyTrend,
  mobileOrderInitialQuickView,
  mobileDispatchSelectedDriverIds,
  mobileDispatchSelectionInitialized,
  mobileDispatchExtraSchedules,
  mobileDispatchExtraSchedulesLoading,
  mobileDispatchExtraSchedulesError,
  orderRangeHelpText,
  onAddAreaToRow,
  onAddLayoutRow,
  onAssignDriver,
  onClearSearch,
  onCreateManualInvoice: handleCreateManualInvoice,
  onCreateManualReceipt,
  onCreateCombinedOrderReceipt,
  onCreateSingleOrderReceipts,
  onCloseCustomerStats,
  onCloseDriverOnboarding,
  onCustomerFilterChange,
  onFetchDriverSchedule,
  onFetchDriverMonthlyStats,
  onFetchDriverOnboarding,
  onFetchBillingDocuments,
  onFetchReceiptDocuments,
  onHideCancelledChange,
  onHideCompletedChange,
  onMonthFilterChange,
  onMoveAreaDown,
  onMoveAreaUp,
  onOpenAdminOrderForm,
  onOpenCreateDriver,
  onOpenCreateUserForm,
  onOpenCustomerPricing,
  onOpenUserProfile,
  onOpenDispatcher,
  onOpenEditDispatcher,
  onOpenEditDriver,
  onOpenExceptionOrder,
  onOpenOrderDetail,
  onOpenQuoteForm,
  onOpenResetPassword,
  onOpenTripCommunication,
  onSettleOrder,
  onAdminOrderCreated,
  onLogout,
  onSelectAdminTab,
  onConfirmRetailBooking,
  onCancelRetailBooking,
  onGeneratePaymentPage,
  onFetchMobileDispatchDriverHistory,
  onRefreshData,
  onRefreshMobileDispatchData,
  onMobileDispatchSelectedDriverIdsChange,
  onMobileDispatchSelectionInitializedChange,
  onMobileOrderInitialQuickViewChange,
  onOpenDriverReportOrders,
  onOrderStatusFilterChange,
  onDownloadSignboard,
  onRemoveAreaFromRow,
  onRemoveLayoutRow,
  onResetFilters,
  onSaveLayoutConfig,
  onSearchKeywordChange,
  onShowDeletedUsersChange,
  onCycleDateSort,
  onToggleUnlinkedCustomerOnly,
  onToggleAllUsers,
  onToggleSelectAll,
  onToggleSelectOrder,
  onSelectOrderGroup,
  onToggleUserSelection,
  onBillingDocumentFiltersChange,
  onReceiptDocumentFiltersChange,
  onDownloadBillingDocument,
  onExportBillingDocuments,
  onRestoreBillingDocument,
  onSearchReceiptOrders,
  openDriverAssignModal,
  orderStatusFilter,
  orders,
  pendingUsers,
  pricingProps,
  recentOrders,
  receiptDocumentFilters,
  receiptDocumentSummary,
  receiptDocuments,
  receiptDocumentsLoading,
  searchKeyword,
  searchMessage,
  searchResults,
  selectedCustomerStats,
  selectedOrderIds,
  selectedUserIds,
  selectAll,
  setBillingFilters,
  setDriverAccountForm,
  setDriverNoticeForm,
  setDriverTrainingConfig,
  setShowDriverNoticeModal,
  setShowDriverTrainingConfig,
  showDeletedUsers,
  showDriverNoticeModal,
  showDriverTrainingConfig,
  sortByDate,
  sortDateDirection,
  statsLoading,
  uniqueCustomers,
  unlinkedCustomerOnly,
  viewHelpers,
  fetchDriverTrainingConfig,
  fetchDriverNotices,
}) {
  if (activeTab === "settlements") {
    return (
      <LazyAdminSection>
        <AdminSettlements settlementProps={settlementProps} user={user} onLogout={onLogout} />
      </LazyAdminSection>
    );
  }

  if (activeTab === "mobileHome") {
    return (
      <AdminMobileDashboard
        orders={orders}
        pendingUsers={pendingUsers}
        driverAccounts={driverAccounts}
        viewHelpers={viewHelpers}
            onAssignDriver={onAssignDriver}
            onDuplicateOrder={handleDuplicateOrder}
            onDeleteOrder={handleDeleteOrder}
            onDownloadSignboard={onDownloadSignboard}
            onEditOrder={viewHelpers?.handleEditOrder}
            onCreateDriverComplaint={handleCreateDriverComplaint}
            onGeneratePaymentPage={onGeneratePaymentPage}
            onApplyCustomerOvertimeCharge={handleApplyCustomerOvertimeCharge}
            onMarkOvertimeHandled={handleMarkOvertimeHandled}
            onOpenAdminOrderForm={onOpenAdminOrderForm}
            onOpenOrderDetail={onOpenOrderDetail}
            onOpenTripCommunication={onOpenTripCommunication}
            onSettleOrder={onSettleOrder}
            onOpenDriverReportOrders={onOpenDriverReportOrders}
            onSelectAdminTab={onSelectAdminTab}
          onLogout={onLogout}
        />
      );
  }

  if (activeTab === "driverConsole") {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <Suspense fallback={null}>
          <DriverConsolePanel apiBaseUrl={apiBaseUrl} />
        </Suspense>
      </div>
    );
  }

  if (activeTab === "mobileProfile") {
    return <AdminMobileProfile onLogout={onLogout} />;
  }

  if (isAdminAppMode && activeTab === "orders") {
    return (
      <AdminMobileOrdersPage
        allUsers={allUsers}
        orders={orders}
            initialQuickView={mobileOrderInitialQuickView}
            viewHelpers={viewHelpers}
            onAssignDriver={onAssignDriver}
            onDuplicateOrder={handleDuplicateOrder}
            onDeleteOrder={handleDeleteOrder}
            onDownloadSignboard={onDownloadSignboard}
            onEditOrder={viewHelpers?.handleEditOrder}
            onCreateDriverComplaint={handleCreateDriverComplaint}
            onGeneratePaymentPage={onGeneratePaymentPage}
            onApplyCustomerOvertimeCharge={handleApplyCustomerOvertimeCharge}
            onMarkOvertimeHandled={handleMarkOvertimeHandled}
            onOpenAdminOrderForm={onOpenAdminOrderForm}
            onOpenOrderDetail={onOpenOrderDetail}
            onOpenTripCommunication={onOpenTripCommunication}
            onSettleOrder={onSettleOrder}
            onInitialQuickViewChange={onMobileOrderInitialQuickViewChange}
            onRefreshData={onRefreshData}
          />
    );
  }

  if (isAdminAppMode && activeTab === "mobileOrderDetail") {
    return (
      <AdminMobileOrderDetailPage
        order={adminMobileOrderDetail}
        viewHelpers={viewHelpers}
        onAssignDriver={onAssignDriver}
        onDuplicateOrder={handleDuplicateOrder}
        onDeleteOrder={handleDeleteOrder}
        onDownloadSignboard={onDownloadSignboard}
        onEditOrder={viewHelpers?.handleEditOrder}
        onCreateDriverComplaint={handleCreateDriverComplaint}
        onGeneratePaymentPage={onGeneratePaymentPage}
        onOpenTripCommunication={onOpenTripCommunication}
        onSettleOrder={onSettleOrder}
        onSelectAdminTab={onSelectAdminTab}
      />
    );
  }

  if (isAdminAppMode && activeTab === "drivers") {
    return (
      <AdminMobileDriversPage
        driverAccounts={driverAccounts}
        driverAccountsError={driverAccountsError}
        driverAccountsLoading={driverAccountsLoading}
        onRetryDrivers={fetchDriverAccounts}
        onOpenCreateDriver={onOpenCreateDriver}
        onOpenEditDriver={onOpenEditDriver}
        onSelectAdminTab={onSelectAdminTab}
      />
    );
  }

  if (isAdminAppMode && activeTab === "mobileDispatch") {
    return (
      <AdminMobileDispatchPage
        orders={orders}
        extraSchedules={mobileDispatchExtraSchedules}
        extraSchedulesLoading={mobileDispatchExtraSchedulesLoading}
        extraSchedulesError={mobileDispatchExtraSchedulesError}
        driverAccounts={driverAccounts}
        driverAccountsError={driverAccountsError}
        driverAccountsLoading={driverAccountsLoading}
        onRetryDrivers={fetchDriverAccounts}
        driverTimeOffByDate={driverTimeOffByDate}
        selectedDriverIds={mobileDispatchSelectedDriverIds}
        hasInitializedDriverSelection={mobileDispatchSelectionInitialized}
        viewHelpers={viewHelpers}
        onFetchDriverHistory={onFetchMobileDispatchDriverHistory}
        onOpenOrderDetail={onOpenOrderDetail}
        onSelectedDriverIdsChange={onMobileDispatchSelectedDriverIdsChange}
        onDriverSelectionInitializedChange={onMobileDispatchSelectionInitializedChange}
        onRefreshData={onRefreshMobileDispatchData}
        onSelectAdminTab={onSelectAdminTab}
      />
    );
  }

  if (isAdminAppMode && activeTab === "mobileDriverManage") {
    return (
      <AdminMobileDriverManagePage
        editingDriverAccount={editingDriverAccount}
        driverAccountForm={driverAccountForm}
        onDriverAccountFormChange={setDriverAccountForm}
        onCreateDriverAccount={handleCreateDriverAccount}
        onUpdateDriverAccount={handleUpdateDriverAccount}
        onSelectAdminTab={onSelectAdminTab}
      />
    );
  }

  if (isAdminAppMode && activeTab === "mobileCreate") {
    return (
      <AdminMobileCreatePage
        allUsers={allUsers}
        areas={pricingProps?.areas || []}
        onOrderCreated={onAdminOrderCreated}
        onSelectAdminTab={onSelectAdminTab}
      />
    );
  }

  if (isAdminAppMode && activeTab === "communications") {
    return (
      <AdminMobileCommunicationPage
        apiBaseUrl={apiBaseUrl}
        communicationOrderId={communicationOrderId}
        communicationRequestNonce={communicationRequestNonce}
      />
    );
  }

  if (isAdminAppMode && activeTab === "users") {
    return (
      <AdminMobileUsersPage
        canViewAcceptance={Boolean(user?.is_master || (user?.permissions?.includes("orders.view") && user?.permissions?.includes("prices.view")))}
        pendingUsers={pendingUsers}
        allUsers={allUsers}
        loading={loading}
        showDeletedUsers={showDeletedUsers}
        selectedUserIds={selectedUserIds}
        onShowDeletedUsersChange={onShowDeletedUsersChange}
        onApproveUser={handleApproveUser}
        onUpdateUserStatus={handleUpdateUserStatus}
        onToggleAllUsers={onToggleAllUsers}
        onToggleUserSelection={onToggleUserSelection}
        onBatchDeleteUsers={handleBatchDeleteUsers}
        onOpenCreateUserForm={onOpenCreateUserForm}
        onOpenResetPassword={onOpenResetPassword}
        onOpenUserProfile={onOpenUserProfile}
        onOpenCustomerPricing={onOpenCustomerPricing}
        onUpdateUserChatSettings={handleUpdateUserChatSettings}
        onUpdateUserRetailMemberBindingSettings={handleUpdateUserRetailMemberBindingSettings}
        onSelectAdminTab={onSelectAdminTab}
      />
    );
  }

  if (activeTab === "users") {
    return (
      <LazyAdminSection>
      <div className="space-y-6">
      <AdminUsers
        apiBaseUrl={apiBaseUrl}
        adminUser={user}
        onUsersChanged={onUsersChanged}
        pendingUsers={pendingUsers}
        allUsers={allUsers}
        loading={loading}
        showDeletedUsers={showDeletedUsers}
        selectedUserIds={selectedUserIds}
        onShowDeletedUsersChange={onShowDeletedUsersChange}
        onApproveUser={handleApproveUser}
        onUpdateUserStatus={handleUpdateUserStatus}
        onToggleAllUsers={onToggleAllUsers}
        onToggleUserSelection={onToggleUserSelection}
        onBatchDeleteUsers={handleBatchDeleteUsers}
        onOpenCreateUserForm={onOpenCreateUserForm}
        onOpenResetPassword={onOpenResetPassword}
        onOpenUserProfile={onOpenUserProfile}
        onOpenCustomerPricing={onOpenCustomerPricing}
        onUpdateUserChatSettings={handleUpdateUserChatSettings}
        onUpdateUserRetailMemberBindingSettings={handleUpdateUserRetailMemberBindingSettings}
      />
      </div>
      </LazyAdminSection>
    );
  }

  if (activeTab === "instructionArchive") return <InstructionArchive admin />;
  if (activeTab === "driverCreatedOrders") return <DriverCreatedOrders />;

  if (activeTab === "orders") {
    return (
      <LazyAdminSection>
      <div className="space-y-5">
        <div>
          <details className="ow-operations"><summary>运营待处理</summary><OperationsExceptionPanel orders={orders} onAssignDriver={onAssignDriver} onOpenExceptionOrder={onOpenExceptionOrder} />
          {orderRangeHelpText && !serviceDateRange?.from && !serviceDateRange?.to && (
            <p className="mt-3 text-xs font-medium text-primary/80">
              {orderRangeHelpText}
            </p>
          )}
          </details>
        </div>
        <OrderManagement
          webLayout
          selectAll={selectAll}
          onToggleSelectAll={onToggleSelectAll}
          serviceDateRange={serviceDateRange}
          onServiceDateRangeChange={onServiceDateRangeChange}
          searchKeyword={searchKeyword}
          isSearching={isSearching}
          isSearchMode={isSearchMode}
          searchMessage={searchMessage}
          searchResults={searchResults.filter(order => matchesServiceDateRange(order, serviceDateRange))}
          orders={orders}
          orderStatusFilter={orderStatusFilter}
          monthFilter={monthFilter}
          customerFilter={viewHelpers.customerFilter}
          sortByDate={sortByDate}
          sortDateDirection={sortDateDirection}
          unlinkedCustomerOnly={unlinkedCustomerOnly}
          hideCompleted={hideCompleted}
          hideCancelled={hideCancelled}
          selectedOrderIds={selectedOrderIds}
          filteredOrderCount={filteredOrders.length}
          uniqueCustomers={uniqueCustomers}
          getOrderTimeCategory={viewHelpers.getOrderTimeCategory}
          isDriverOvertimeUnresolvedOrder={viewHelpers.isDriverOvertimeUnresolvedOrder}
          isMissingDriverOrder={viewHelpers.isMissingDriverOrder}
          onSearchKeywordChange={onSearchKeywordChange}
          onSearchKeyDown={handleSearchKeyDown}
          onSearchOrders={handleSearchOrders}
          onClearSearch={onClearSearch}
          onOrderStatusFilterChange={onOrderStatusFilterChange}
          onMonthFilterChange={onMonthFilterChange}
          onCustomerFilterChange={onCustomerFilterChange}
          onCycleDateSort={onCycleDateSort}
          onToggleUnlinkedCustomerOnly={onToggleUnlinkedCustomerOnly}
          onHideCompletedChange={onHideCompletedChange}
          onHideCancelledChange={onHideCancelledChange}
          onOpenAdminOrderForm={onOpenAdminOrderForm}
          onResetFilters={onResetFilters}
          onBulkDelete={handleBulkDelete}
          onBulkMarkPaid={handleBulkMarkPaid}
          isMarkingPaid={isMarkingPaid}
          onGeneratePaymentPage={onGeneratePaymentPage}
        >
        <div className="p-6" data-testid="admin-order-results" role="region" aria-label="订单列表" tabIndex={0}>
          <AdminOrderList
            orderStatusFilter={orderStatusFilter}
            isMissingDriverOrder={viewHelpers.isMissingDriverOrder}
            isSearchMode={isSearchMode}
            searchResults={searchResults.filter(order => matchesServiceDateRange(order, serviceDateRange))}
            filteredOrders={filteredOrders}
            groupedOrders={mergeDriverTimeOffGroups(
              viewHelpers.groupOrdersByDate(filteredOrders, sortDateDirection),
              driverTimeOffByDate,
              {
                includeTimeOffOnlyDates: orderStatusFilter === "all",
                hidePastTimeOffOnlyDates: Boolean(viewHelpers.customerFilter),
              },
            ).filter(group => matchesServiceDateRange({departure_date:group.date}, serviceDateRange))}
            selectedOrderIds={selectedOrderIds}
            selectAll={selectAll}
            isOrderIncomplete={isOrderIncomplete}
            formatPickupDisplay={viewHelpers.formatPickupDisplay}
            formatDestinationDisplay={viewHelpers.formatDestinationDisplay}
            getStatusBadge={viewHelpers.getStatusBadge}
            getTimeCategoryBadge={viewHelpers.getTimeCategoryBadge}
            getCharterTimeStatus={viewHelpers.getCharterTimeStatus}
            getVehicleTypeName={viewHelpers.getVehicleTypeName}
            driverTimeOffByDate={driverTimeOffByDate}
            onToggleSelectAll={onToggleSelectAll}
            onToggleSelectOrder={onToggleSelectOrder}
            onSelectOrderGroup={onSelectOrderGroup}
            onQuoteOrder={onOpenQuoteForm}
            onEditOrder={viewHelpers.handleEditOrder}
            onDuplicateOrder={handleDuplicateOrder}
            onDeleteOrder={handleDeleteOrder}
            onRestoreOrder={handleRestoreOrder}
            onAssignDriver={openDriverAssignModal}
            onExportOrder={exportOrderInfoJapanese}
            onDownloadSignboard={onDownloadSignboard}
            onUnlockOrder={handleUnlockOrder}
            onApplyCustomerOvertimeCharge={handleApplyCustomerOvertimeCharge}
            onMarkOvertimeHandled={handleMarkOvertimeHandled}
            onOpenTripCommunication={onOpenTripCommunication}
            onNotifyAssignedDriver={handleNotifyAssignedDriver}
            onPutOrderIntoPool={handlePutOrderIntoPool}
            onWithdrawOrderFromPool={handleWithdrawOrderFromPool}
            onCreateDriverComplaint={handleCreateDriverComplaint}
            onResolveDriverIssue={handleResolveDriverIssue}
            onConfirmRetailBooking={onConfirmRetailBooking}
            onCancelRetailBooking={onCancelRetailBooking}
            onSettleOrder={onSettleOrder}
          />
        </div>
        </OrderManagement>
      </div>
      </LazyAdminSection>
    );
  }

  if (activeTab === "billing") {
    return (
      <LazyAdminSection>
      <BillingExport
        allUsers={allUsers}
        apiBaseUrl={apiBaseUrl}
        billingFilters={billingFilters}
        loading={loading}
        onBillingFiltersChange={setBillingFilters}
        onExportBilling={handleExportBilling}
        onOpenOrder={onOpenExceptionOrder}
      />
      </LazyAdminSection>
    );
  }

  if (activeTab === "billingDocuments") {
    return (
      <LazyAdminSection>
      <BillingDocumentManagement
        allUsers={allUsers}
        documents={billingDocuments}
        filters={billingDocumentFilters}
        loading={loading || billingDocumentsLoading}
        receiptDocumentFilters={receiptDocumentFilters}
        receiptDocumentSummary={receiptDocumentSummary}
        receiptDocuments={receiptDocuments}
        receiptDocumentsLoading={receiptDocumentsLoading}
        summary={billingDocumentSummary}
        onDeleteDocument={handleDeleteBillingDocument}
        onCreateManualInvoice={handleCreateManualInvoice}
        onCreateManualReceipt={onCreateManualReceipt}
        onCreateCombinedOrderReceipt={onCreateCombinedOrderReceipt}
        onCreateSingleOrderReceipts={onCreateSingleOrderReceipts}
        onSearchReceiptOrders={onSearchReceiptOrders}
        onDownloadDocument={onDownloadBillingDocument}
        onExportDocuments={onExportBillingDocuments}
        onFetchDocuments={onFetchBillingDocuments}
        onFetchReceiptDocuments={onFetchReceiptDocuments}
        onFiltersChange={onBillingDocumentFiltersChange}
        onReceiptDocumentFiltersChange={onReceiptDocumentFiltersChange}
        onRestoreDocument={onRestoreBillingDocument}
      />
      </LazyAdminSection>
    );
  }

  if (activeTab === "communications") {
    return (
      <LazyAdminSection>
        <TripCommunicationCenter
          apiBaseUrl={apiBaseUrl}
          initialOrderId={communicationOrderId}
          initialOrderRequestNonce={communicationRequestNonce}
        />
      </LazyAdminSection>
    );
  }

  if (activeTab === "retailRewards") {
    return (
      <LazyAdminSection>
        <RetailRewardsAdmin />
      </LazyAdminSection>
    );
  }

  if (activeTab === "cityride") {
    return (
      <LazyAdminSection>
        <CityRideGmailImport apiBaseUrl={apiBaseUrl} />
      </LazyAdminSection>
    );
  }

  if (activeTab === "pricing") {
    return (
      <LazyAdminSection>
        <PricingConfiguration apiBaseUrl={apiBaseUrl} loading={loading} {...pricingProps} />
      </LazyAdminSection>
    );
  }

  if (activeTab === "layout") {
    return (
      <LazyAdminSection>
      <FrontendLayoutConfig
        areas={pricingProps.areas}
        layoutConfig={layoutConfig}
        layoutLoading={layoutLoading}
        onSave={onSaveLayoutConfig}
        onAddRow={onAddLayoutRow}
        onRemoveRow={onRemoveLayoutRow}
        onAddAreaToRow={onAddAreaToRow}
        onRemoveAreaFromRow={onRemoveAreaFromRow}
        onMoveAreaUp={onMoveAreaUp}
        onMoveAreaDown={onMoveAreaDown}
      />
      </LazyAdminSection>
    );
  }

  if (activeTab === "daily") {
    return (
      <LazyAdminSection>
      <DailyReport
        recentOrders={recentOrders}
        statsLoading={statsLoading}
        selectedOrderIds={selectedOrderIds}
        isOrderIncomplete={isOrderIncomplete}
        viewHelpers={viewHelpers}
        onToggleSelectOrder={onToggleSelectOrder}
        onQuoteOrder={onOpenQuoteForm}
        onEditOrder={viewHelpers.handleEditOrder}
        onDuplicateOrder={handleDuplicateOrder}
        onDeleteOrder={handleDeleteOrder}
        onRestoreOrder={handleRestoreOrder}
        onAssignDriver={openDriverAssignModal}
        onExportOrder={exportOrderInfoJapanese}
        onDownloadSignboard={onDownloadSignboard}
        onUnlockOrder={handleUnlockOrder}
        onApplyCustomerOvertimeCharge={handleApplyCustomerOvertimeCharge}
        onMarkOvertimeHandled={handleMarkOvertimeHandled}
        onOpenTripCommunication={onOpenTripCommunication}
        onNotifyAssignedDriver={handleNotifyAssignedDriver}
        onPutOrderIntoPool={handlePutOrderIntoPool}
        onWithdrawOrderFromPool={handleWithdrawOrderFromPool}
        onCreateDriverComplaint={handleCreateDriverComplaint}
        onConfirmRetailBooking={onConfirmRetailBooking}
        onCancelRetailBooking={onCancelRetailBooking}
        onSettleOrder={onSettleOrder}
        onBulkDelete={handleBulkDelete}
        onGeneratePaymentPage={onGeneratePaymentPage}
      />
      </LazyAdminSection>
    );
  }

  if (activeTab === "stats") {
    return (
      <LazyAdminSection>
      <AdminStats
        apiBaseUrl={apiBaseUrl}
        statsLoading={statsLoading}
        dashboardStats={dashboardStats}
        monthlyTrend={monthlyTrend}
        selectedCustomerStats={selectedCustomerStats}
        onViewCustomerStats={viewHelpers.fetchCustomerStats}
        onCloseCustomerStats={onCloseCustomerStats}
      />
      </LazyAdminSection>
    );
  }

  if (activeTab === "attendance") {
    return <LazyAdminSection><AdminAttendanceCenter/></LazyAdminSection>;
  }

  if (activeTab === "drivers") {
    return (
      <LazyAdminSection>
      <AdminDrivers
        adminUser={user}
        dispatchers={dispatchers}
        driverAccounts={driverAccounts}
        driverMonthlyStats={driverMonthlyStats}
        driverMonthlyStatsDispatcherId={driverMonthlyStatsDispatcherId}
        driverMonthlyStatsDriverId={driverMonthlyStatsDriverId}
        driverMonthlyStatsLoading={driverMonthlyStatsLoading}
        driverMonthlyStatsMonth={driverMonthlyStatsMonth}
        driverNoticeForm={driverNoticeForm}
        driverNotices={driverNotices}
        driverOnboardingLoading={driverOnboardingLoading}
        driverTrainingConfig={driverTrainingConfig}
        selectedDriverOnboarding={selectedDriverOnboarding}
        showDriverTrainingConfig={showDriverTrainingConfig}
        showDriverAccountForm={viewHelpers.showDriverAccountForm}
        editingDriverAccount={editingDriverAccount}
        driverAccountForm={driverAccountForm}
        onDriverAccountFormChange={setDriverAccountForm}
        onOpenCreateDriver={onOpenCreateDriver}
        onOpenEditDriver={onOpenEditDriver}
        onCloseDriverForm={viewHelpers.closeDriverAccountForm}
        onCreateDriverAccount={handleCreateDriverAccount}
        onUpdateDriverAccount={handleUpdateDriverAccount}
        onResetDriverPassword={handleResetDriverPassword}
        onToggleDriverActive={handleToggleDriverActive}
        onToggleDriverInternalDispatchOverview={handleToggleDriverInternalDispatchOverview}
        onToggleDriverPinned={handleToggleDriverPinned}
        onToggleDriverRevenueStats={handleToggleDriverRevenueStats}
        onDeleteDriverAccount={handleDeleteDriverAccount}
        onDriverMonthlyStatsDispatcherChange={handleDriverMonthlyStatsDispatcherChange}
        onDriverMonthlyStatsDriverChange={handleDriverMonthlyStatsDriverChange}
        onDriverMonthlyStatsMonthChange={handleDriverMonthlyStatsMonthChange}
        onExportDriverMonthlyStats={handleExportDriverMonthlyStats}
        onFetchDriverMonthlyStats={onFetchDriverMonthlyStats}
        onSubmitDriverMonthlySettlements={handleSubmitDriverMonthlySettlements}
        onUpdateDriverMonthlyDispatchPrice={handleUpdateDriverMonthlyDispatchPrice}
        onMigrateDriverOrders={handleMigrateDriverOrders}
        fetchDriverTrainingConfig={fetchDriverTrainingConfig}
        fetchDriverNotices={fetchDriverNotices}
        setShowDriverTrainingConfig={setShowDriverTrainingConfig}
        setShowDriverNoticeModal={setShowDriverNoticeModal}
        setDriverNoticeForm={setDriverNoticeForm}
        setDriverTrainingConfig={setDriverTrainingConfig}
        handleSaveDriverTrainingConfig={handleSaveDriverTrainingConfig}
        handleEditDriverNotice={handleEditDriverNotice}
        handleSaveDriverNotice={handleSaveDriverNotice}
        handleTranslateDriverNotice={handleTranslateDriverNotice}
        driverNoticeTranslating={driverNoticeTranslating}
        showDriverNoticeModal={showDriverNoticeModal}
        editingDriverNotice={editingDriverNotice}
        onFetchDriverSchedule={onFetchDriverSchedule}
        onFetchDriverOnboarding={onFetchDriverOnboarding}
        onReviewDriverOnboarding={handleReviewDriverOnboarding}
        onCloseDriverOnboarding={onCloseDriverOnboarding}
      />
      </LazyAdminSection>
    );
  }

  if (activeTab === "admins") {
    return (
      <LazyAdminSection>
      <AdminDispatchers
        dispatchers={dispatchers}
        loading={dispatchersLoading}
        onCreateDispatcher={onOpenDispatcher}
        onEditDispatcher={onOpenEditDispatcher}
        onToggleDispatcher={handleToggleDispatcherActive}
        onDeleteDispatcher={handleDeleteDispatcher}
      />
      </LazyAdminSection>
    );
  }

  if(activeTab==="audit") return <LazyAdminSection><AdminAuditLogs apiBaseUrl={apiBaseUrl}/></LazyAdminSection>;

  return null;
}
